import React, { useEffect, useState, memo, useRef, useCallback, useMemo } from 'react';
import {
  GoogleMap,
  TrafficLayer,
  DirectionsService,
  DirectionsRenderer,
  OverlayView,
  Polygon,
  Circle,
  DrawingManager,
  Polyline,
  InfoWindow
} from '@react-google-maps/api';
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import AddLocationAltOutlinedIcon from "@mui/icons-material/AddLocationAltOutlined";
import PolylineOutlinedIcon from "@mui/icons-material/PolylineOutlined";
import SettingsRemoteOutlinedIcon from "@mui/icons-material/SettingsRemoteOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { getResolvedLiveSnapshot, isValidGPS } from '../../utils/vehicleStatus';
import { resolveUiDeviceStatus } from '../../utils/uiDeviceStatus';
import { resolveTrackingState } from '../../utils/trackingStateEngine';
import { useSocket } from '../../socket/SocketContext';
import { useGetTripsQuery } from '../../redux/logistics/logisticsApi';
import {
  buildFleetClusters,
  getMapViewportSnapshot,
  isValidMapCoordinate,
} from '../../utils/mapClustering';
import { beginProfile } from '../../utils/renderProfiler';
import { useDevRenderInstrumentation } from '../../utils/devInstrumentation';
// Icons
import CarBlack from '../../assets/accessories/car svg/Car black.png';
import CarBlueAcc from '../../assets/accessories/car svg/Car blue.png';
import CarGreenAcc from '../../assets/accessories/car svg/Car green.png';
import CarRedAcc from '../../assets/accessories/car svg/Car Red.png';
import CarYellowAcc from '../../assets/accessories/car svg/Car yellow.png';
import TruckBlack from '../../assets/accessories/Truck svg/Truck black.png';
import TruckBlueAcc from '../../assets/accessories/Truck svg/Truck blue.png';
import TruckGreenAcc from '../../assets/accessories/Truck svg/Truck green.png';
import TruckRedAcc from '../../assets/accessories/Truck svg/truck red.png';
import TruckYellowAcc from '../../assets/accessories/Truck svg/Truck yellow.png';
import BusBlack from '../../assets/accessories/bus svg/bus black.png';
import BusBlueAcc from '../../assets/accessories/bus svg/bus blue.png';
import BusGreenAcc from '../../assets/accessories/bus svg/bus green.png';
import BusRedAcc from '../../assets/accessories/bus svg/bus red.png';
import BusYellowAcc from '../../assets/accessories/bus svg/bus yellow.png';
import BikeBlack from '../../assets/accessories/bike svg/Bike black.png';
import BikeBlueAcc from '../../assets/accessories/bike svg/Bike blue.png';
import BikeGreenAcc from '../../assets/accessories/bike svg/Bike green.png';
import BikeRedAcc from '../../assets/accessories/bike svg/Bike red.png';
import BikeYellowAcc from '../../assets/accessories/bike svg/Bike yellow.png';
import VanBlack from '../../assets/accessories/van svg/van black.png';
import VanBlueAcc from '../../assets/accessories/van svg/van blue.png';
import VanGreenAcc from '../../assets/accessories/van svg/van green.png';
import VanRedAcc from '../../assets/accessories/van svg/van red.png';
import VanYellowAcc from '../../assets/accessories/van svg/van yellow.png';
import AmbulanceBlack from '../../assets/accessories/Ambulance svg/Ambulance black.png';
import AmbulanceBlueAcc from '../../assets/accessories/Ambulance svg/Ambulance Blue.png';
import AmbulanceGreenAcc from '../../assets/accessories/Ambulance svg/Ambulance Green.png';
import AmbulanceRedAcc from '../../assets/accessories/Ambulance svg/Ambulance Red.png';
import AmbulanceYellowAcc from '../../assets/accessories/Ambulance svg/Ambulance Yellow.png';
import PolicecarBlack from '../../assets/accessories/policecar svg/policecar black.png';
import PolicecarBlueAcc from '../../assets/accessories/policecar svg/policecar blue.png';
import PolicecarGreenAcc from '../../assets/accessories/policecar svg/policecar green.png';
import PolicecarRedAcc from '../../assets/accessories/policecar svg/policecar red.png';
import PolicecarYellowAcc from '../../assets/accessories/policecar svg/policecar yellow.png';
import CraneBlack from '../../assets/accessories/crane svg/crane black.png';
import CraneBlueAcc from '../../assets/accessories/crane svg/crane blue.png';
import CraneGreenAcc from '../../assets/accessories/crane svg/crane green.png';
import CraneRedAcc from '../../assets/accessories/crane svg/crane red.png';
import CraneYellowAcc from '../../assets/accessories/crane svg/crane yellow.png';
import JcbBlack from '../../assets/accessories/jcb svg/jcb black.png';
import JcbBlueAcc from '../../assets/accessories/jcb svg/jcb blue.png';
import JcbGreenAcc from '../../assets/accessories/jcb svg/jcb green.png';
import JcbRedAcc from '../../assets/accessories/jcb svg/jcb red.png';
import JcbYellowAcc from '../../assets/accessories/jcb svg/jcb yellow.png';
import SchoolbusBlack from '../../assets/accessories/Schoolbus svg/schoolbus black.png';
import SchoolbusBlueAcc from '../../assets/accessories/Schoolbus svg/schoolbus blue.png';
import SchoolbusGreenAcc from '../../assets/accessories/Schoolbus svg/schoolbus green.png';
import SchoolbusRedAcc from '../../assets/accessories/Schoolbus svg/schoolbus red.png';
import SchoolbusYellowAcc from '../../assets/accessories/Schoolbus svg/schoolbus yellow.png';
import TractorBlack from '../../assets/accessories/Tracktor svg/Tracktor black.png';
import TractorBlueAcc from '../../assets/accessories/Tracktor svg/tracktor blue.png';
import TractorGreenAcc from '../../assets/accessories/Tracktor svg/Tracktor Green.png';
import TractorRedAcc from '../../assets/accessories/Tracktor svg/Tracktor Red.png';
import TractorYellowAcc from '../../assets/accessories/Tracktor svg/Tracktor Yellow.png';
import PickupBlack from '../../assets/accessories/pickup svg/pickup black.png';
import PickupBlueAcc from '../../assets/accessories/pickup svg/pickup blue.png';
import PickupGreenAcc from '../../assets/accessories/pickup svg/pickup green.png';
import PickupRedAcc from '../../assets/accessories/pickup svg/pickup red.png';
import PickupYellowAcc from '../../assets/accessories/pickup svg/pickup yellow.png';
import TripPlaybackLayer from './TripPlaybackLayer';
import AutoBlack from '../../assets/accessories/Auto svg/Auto black.png';
import AutoBlueAcc from '../../assets/accessories/Auto svg/Auto blue.png';
import AutoGreenAcc from '../../assets/accessories/Auto svg/Auto green.png';
import AutoRedAcc from '../../assets/accessories/Auto svg/Auto Red.png';
import AutoYellowAcc from '../../assets/accessories/Auto svg/Auto Yellow.png';
import CarPurpleAcc from '../../assets/accessories/car svg/Car purple.png';

const containerStyle = { width: '100%', height: '100%' };
const defaultCenter = { lat: 22.5937, lng: 78.9629 };
const DEFAULT_INDIA_ZOOM = 5;
const CLUSTER_MAX_AUTO_ZOOM = 15;
const WEB_MAP_MAX_VISIBLE_MARKERS = 5000;
const LIVE_VISUAL_EXTRAPOLATION_MAX_MS = 4000;
const LIVE_VISUAL_EXTRAPOLATION_START_MS = 150;
const LIVE_VISUAL_MIN_SEGMENT_MS = 700;
const LIVE_VISUAL_MAX_SEGMENT_MS = 12000;
const LIVE_VISUAL_SNAP_LOCK_MAX_MS = 650;
const LIVE_VISUAL_EXPECTED_GAP_MIN_MS = 2500;
const LIVE_VISUAL_EXPECTED_GAP_MAX_MS = 20000;
const LIVE_VISUAL_EXPECTED_GAP_BUFFER_RATIO = 1.18;
const LIVE_VISUAL_ACTIVE_DELTA_MIN_M = 0.12;
const LIVE_VISUAL_RECENT_PACKET_WINDOW_MS = 5000;
const FOLLOW_PAN_MIN_METERS = 2;
const FOLLOW_PAN_EASE = 0.18;
const FOLLOW_SAFE_VIEWPORT_RATIO = 0.18;
const POPUP_FOCUS_ANCHOR_Y_RATIO = 0.68;
const FOLLOW_MOVING_RECENTER_X_RATIO = 0.2;
const FOLLOW_MOVING_RECENTER_Y_RATIO = 0.2;
const TURN_PATH_MIN_METERS = 10;
const TURN_PATH_MIN_DELTA_DEG = 28;
const GOOGLE_WORLD_COORD_WIDTH = 256;
const FOLLOW_EDGE_TRIGGER_X_RATIO = 0.06;
const FOLLOW_EDGE_TRIGGER_Y_RATIO = 0.08;
const FOLLOW_POPUP_TOP_RESERVED_RATIO = 0.3;
const LIVE_VISUAL_DEBUG_WINDOW_FLAG = "__GPS_LIVE_DEBUG__";

const bearingBetween = (a, b) => {
  if (!a || !b) return null;
  const lat1 = Number(a.lat);
  const lng1 = Number(a.lng);
  const lat2 = Number(b.lat);
  const lng2 = Number(b.lng);
  if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return null;
  if (lat1 === lat2 && lng1 === lng2) return null;

  const toRad = (deg) => (deg * Math.PI) / 180;
  const toDeg = (rad) => (rad * 180) / Math.PI;
  const phi1 = toRad(lat1);
  const phi2 = toRad(lat2);
  const dLon = toRad(lng2 - lng1);
  const y = Math.sin(dLon) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLon);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

const normalizeDeg = (deg) => ((deg % 360) + 360) % 360;

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const pickFinite = (...values) => {
  for (const value of values) {
    const parsed = toFiniteNumber(value);
    if (parsed !== null) return parsed;
  }
  return null;
};

const pickTelemetryFromSources = (sources = [], aliases = []) => {
  for (const source of sources) {
    for (const alias of aliases) {
      const parsed = toFiniteNumber(source?.[alias]);
      if (parsed !== null) return parsed;
    }
  }
  return null;
};

const getSocketLocationMeta = (socketData = {}) => {
  const latitude = pickFinite(socketData?.latitude, socketData?.lat);
  const longitude = pickFinite(socketData?.longitude, socketData?.lng);
  const hasCoords = isValidGPS(Number(latitude), Number(longitude));
  const inherited = socketData?.locationInherited === true || socketData?.hasLiveLocation === false;
  return {
    latitude,
    longitude,
    hasCoords,
    hasLiveCoords: hasCoords && !inherited,
    hasRenderableCoords: hasCoords,
  };
};

const toTimestampMs = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return 0;
    if (/^\d+$/.test(trimmed)) {
      const num = Number(trimmed);
      if (Number.isFinite(num)) return num < 1e12 ? num * 1000 : num;
    }
  }
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatAddress = (address) => {
  if (!address) return null;
  if (typeof address === "string") return address;
  if (address.full) return address.full;

  const parts = [];
  if (address.street) parts.push(address.street);
  if (address.city) parts.push(address.city);
  if (address.state) parts.push(address.state);
  if (address.country) parts.push(address.country);

  return parts.length > 0 ? parts.join(", ") : null;
};

const normalizeAddressText = (value) => {
  const text = formatAddress(value);
  if (!text) return null;
  const normalized = text.trim();
  if (
    /^location at\s*-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/i.test(normalized) ||
    /^(unknown location|unknown address|location unavailable|address unavailable|resolving address\.{0,3}|loading address\.{0,3}|n\/a|na|null)$/i.test(normalized)
  ) {
    return null;
  }
  return normalized;
};

const haversineMeters = (a, b) => {
  if (!a || !b) return 0;
  const lat1 = Number(a.lat);
  const lng1 = Number(a.lng);
  const lat2 = Number(b.lat);
  const lng2 = Number(b.lng);
  if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return 0;
  if (lat1 === lat2 && lng1 === lng2) return 0;

  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const x =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  return R * c;
};

const shortestDeltaDeg = (fromDeg, toDeg) =>
  ((normalizeDeg(toDeg) - normalizeDeg(fromDeg) + 540) % 360) - 180;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const resolveLiveSpeed = (value, fallback = 0) => {
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed >= 0) return parsed;
  const fallbackParsed = Number(fallback);
  return Number.isFinite(fallbackParsed) && fallbackParsed >= 0 ? fallbackParsed : 0;
};

const isStationaryStatus = (status) => {
  const key = String(status || "").trim().toLowerCase();
  return ["idle", "stopped", "inactive", "expired", "offline", "no_data", "nodata"].includes(key);
};

const interpolateHeading = (fromDeg, toDeg, t) =>
  normalizeDeg(normalizeDeg(fromDeg) + shortestDeltaDeg(fromDeg, toDeg) * clamp(t, 0, 1));

const interpolatePoint = (from, to, t) => ({
  lat: Number(from?.lat) + (Number(to?.lat) - Number(from?.lat)) * clamp(t, 0, 1),
  lng: Number(from?.lng) + (Number(to?.lng) - Number(from?.lng)) * clamp(t, 0, 1),
});

const getMapCenterForMarkerAnchor = (
  map,
  position,
  { anchorXRatio = 0.5, anchorYRatio = 0.5 } = {}
) => {
  if (!map || !position || !window.google?.maps?.LatLng || !window.google?.maps?.Point) {
    return position || null;
  }

  const projection = map.getProjection?.();
  const center = map.getCenter?.();
  const zoom = Number(map.getZoom?.());
  const mapDiv = map.getDiv?.();
  const width = Number(mapDiv?.clientWidth || mapDiv?.offsetWidth || 0);
  const height = Number(mapDiv?.clientHeight || mapDiv?.offsetHeight || 0);
  const lat = Number(position?.lat);
  const lng = Number(position?.lng);

  if (
    !projection?.fromLatLngToPoint ||
    !projection?.fromPointToLatLng ||
    !center ||
    !Number.isFinite(zoom) ||
    width <= 0 ||
    height <= 0 ||
    !isValidGPS(lat, lng)
  ) {
    return position || null;
  }

  const centerWorld = projection.fromLatLngToPoint(center);
  const markerWorld = projection.fromLatLngToPoint(
    new window.google.maps.LatLng(lat, lng)
  );
  if (!centerWorld || !markerWorld) return position || null;

  const scale = 2 ** zoom;
  const desiredX = width * clamp(anchorXRatio, 0.1, 0.9);
  const desiredY = height * clamp(anchorYRatio, 0.1, 0.9);
  let centerWorldX = markerWorld.x - (desiredX - width / 2) / scale;
  const centerWorldY = markerWorld.y - (desiredY - height / 2) / scale;

  if (centerWorldX < 0 || centerWorldX > GOOGLE_WORLD_COORD_WIDTH) {
    centerWorldX =
      ((centerWorldX % GOOGLE_WORLD_COORD_WIDTH) + GOOGLE_WORLD_COORD_WIDTH) %
      GOOGLE_WORLD_COORD_WIDTH;
  }
  const clampedCenterWorldY = clamp(centerWorldY, 0, GOOGLE_WORLD_COORD_WIDTH);

  const anchoredCenter = projection.fromPointToLatLng(
    new window.google.maps.Point(centerWorldX, clampedCenterWorldY)
  );
  if (!anchoredCenter) return position || null;

  return {
    lat: anchoredCenter.lat(),
    lng: anchoredCenter.lng(),
  };
};

const getMarkerViewportState = (
  map,
  position,
  {
    popupOpen = false,
    leftEdgeRatio = FOLLOW_EDGE_TRIGGER_X_RATIO,
    rightEdgeRatio = FOLLOW_EDGE_TRIGGER_X_RATIO,
    topEdgeRatio = FOLLOW_EDGE_TRIGGER_Y_RATIO,
    bottomEdgeRatio = FOLLOW_EDGE_TRIGGER_Y_RATIO,
  } = {}
) => {
  if (!map || !position || !window.google?.maps?.LatLng) return null;

  const projection = map.getProjection?.();
  const center = map.getCenter?.();
  const zoom = Number(map.getZoom?.());
  const mapDiv = map.getDiv?.();
  const width = Number(mapDiv?.clientWidth || mapDiv?.offsetWidth || 0);
  const height = Number(mapDiv?.clientHeight || mapDiv?.offsetHeight || 0);
  const lat = Number(position?.lat);
  const lng = Number(position?.lng);

  if (
    !projection?.fromLatLngToPoint ||
    !center ||
    !Number.isFinite(zoom) ||
    width <= 0 ||
    height <= 0 ||
    !isValidGPS(lat, lng)
  ) {
    return null;
  }

  const centerWorld = projection.fromLatLngToPoint(center);
  const markerWorld = projection.fromLatLngToPoint(
    new window.google.maps.LatLng(lat, lng)
  );
  if (!centerWorld || !markerWorld) return null;

  const scale = 2 ** zoom;
  let dxWorld = markerWorld.x - centerWorld.x;
  if (dxWorld > GOOGLE_WORLD_COORD_WIDTH / 2) dxWorld -= GOOGLE_WORLD_COORD_WIDTH;
  if (dxWorld < -GOOGLE_WORLD_COORD_WIDTH / 2) dxWorld += GOOGLE_WORLD_COORD_WIDTH;
  const dyWorld = markerWorld.y - centerWorld.y;

  const pixel = {
    x: width / 2 + dxWorld * scale,
    y: height / 2 + dyWorld * scale,
  };

  const safeLeftRatio = clamp(leftEdgeRatio, 0.02, 0.48);
  const safeRightRatio = clamp(rightEdgeRatio, 0.02, 0.48);
  const safeTopRatio = clamp(topEdgeRatio, 0.02, 0.48);
  const safeBottomRatio = clamp(bottomEdgeRatio, 0.02, 0.48);
  const leftEdge = width * safeLeftRatio;
  const rightEdge = width * (1 - safeRightRatio);
  const topEdge = height * (popupOpen ? Math.max(FOLLOW_POPUP_TOP_RESERVED_RATIO, safeTopRatio) : safeTopRatio);
  const bottomEdge = height * (1 - safeBottomRatio);

  return {
    pixel,
    width,
    height,
    leftEdge,
    rightEdge,
    topEdge,
    bottomEdge,
    outsideSafeZone:
      pixel.x <= leftEdge ||
      pixel.x >= rightEdge ||
      pixel.y <= topEdge ||
      pixel.y >= bottomEdge,
  };
};

const resolveTurnPivot = ({ from, to, fromHeading, toHeading, movedM }) => {
  const turnDelta = Math.abs(shortestDeltaDeg(fromHeading, toHeading));
  if (!Number.isFinite(turnDelta) || !Number.isFinite(movedM)) return null;
  if (movedM < TURN_PATH_MIN_METERS || turnDelta < TURN_PATH_MIN_DELTA_DEG) return null;

  const leadDistance = clamp(movedM * 0.35, 6, 35);
  const exitDistance = clamp(movedM * 0.28, 4, 24);
  const pivotFrom = projectPoint(from, fromHeading, leadDistance);
  const pivotTo = projectPoint(to, normalizeDeg(toHeading + 180), exitDistance);
  if (!pivotFrom || !pivotTo) return null;
  if (!isValidGPS(Number(pivotFrom.lat), Number(pivotFrom.lng))) return null;
  if (!isValidGPS(Number(pivotTo.lat), Number(pivotTo.lng))) return null;

  return {
    lat: (Number(pivotFrom.lat) + Number(pivotTo.lat)) / 2,
    lng: (Number(pivotFrom.lng) + Number(pivotTo.lng)) / 2,
  };
};

const resolveVisualDurationMs = ({ packetGapMs, arrivalGapMs, movedM, speedKmh }) => {
  const packetGap = Number(packetGapMs);
  const arrivalGap = Number(arrivalGapMs);
  const candidateGap = Math.max(
    Number.isFinite(packetGap) ? packetGap : 0,
    Number.isFinite(arrivalGap) ? arrivalGap : 0
  );
  if (Number.isFinite(candidateGap) && candidateGap >= LIVE_VISUAL_MIN_SEGMENT_MS) {
    const bufferedGap = Math.max(candidateGap * 0.92, LIVE_VISUAL_MIN_SEGMENT_MS);
    return clamp(bufferedGap, LIVE_VISUAL_MIN_SEGMENT_MS, LIVE_VISUAL_MAX_SEGMENT_MS);
  }

  const safeSpeedKmh = Math.max(Number(speedKmh || 0), 4);
  const speedBasedMs = movedM > 0 ? (movedM / (safeSpeedKmh / 3.6)) * 1000 : LIVE_VISUAL_MIN_SEGMENT_MS;
  return clamp(Math.max(speedBasedMs || 900, LIVE_VISUAL_MIN_SEGMENT_MS), LIVE_VISUAL_MIN_SEGMENT_MS, LIVE_VISUAL_MAX_SEGMENT_MS);
};

const projectPoint = (start, headingDeg, distanceMeters) => {
  const lat = Number(start?.lat);
  const lng = Number(start?.lng);
  const heading = Number(headingDeg);
  const distance = Number(distanceMeters);
  if (![lat, lng, heading, distance].every(Number.isFinite)) return null;
  if (distance <= 0) return { lat, lng };

  const R = 6371000;
  const brng = (heading * Math.PI) / 180;
  const phi1 = (lat * Math.PI) / 180;
  const lambda1 = (lng * Math.PI) / 180;
  const delta = distance / R;

  const sinPhi2 =
    Math.sin(phi1) * Math.cos(delta) +
    Math.cos(phi1) * Math.sin(delta) * Math.cos(brng);
  const phi2 = Math.asin(clamp(sinPhi2, -1, 1));
  const y = Math.sin(brng) * Math.sin(delta) * Math.cos(phi1);
  const x = Math.cos(delta) - Math.sin(phi1) * Math.sin(phi2);
  const lambda2 = lambda1 + Math.atan2(y, x);

  return {
    lat: (phi2 * 180) / Math.PI,
    lng: (((lambda2 * 180) / Math.PI + 540) % 360) - 180,
  };
};

const isLiveVisualDebugEnabled = () => {
  if (typeof window === "undefined") return false;
  if (window[LIVE_VISUAL_DEBUG_WINDOW_FLAG] === true) return true;
  try {
    return window.localStorage?.getItem("gps_live_visual_debug") === "1";
  } catch {
    return false;
  }
};

const logLiveVisualDebug = (event, payload = {}) => {
  if (!isLiveVisualDebugEnabled()) return;
  console.debug(
    `[live-visual] ${event}`,
    typeof payload === "function" ? payload() : payload
  );
};

const ACCESSORY_TYPE_ALIASES = {
  mountainjeep: 'car',
  jeep: 'car',
  taxi: 'car',
  taxi1: 'car',
  supercar: 'car',
  exporter: 'truck',
  cargoship: 'boat',
  custom: 'car',
  cycle: 'bike',
  motorcycle: 'bike',
  scooter: 'scooty',
  scootor: 'scooty',
  rickshaw: 'auto',
  autorickshaw: 'auto',
  police: 'policecar',
  policecar: 'policecar',
  policebike: 'policebike',
  policebus: 'policebus',
  lorry: 'truck',
  trailer: 'truck',
  dumper: 'dumpher',
  tipper: 'dumpher',
  earthmover: 'bulldozer',
  school_bus: 'schoolbus',
  schoolbus: 'schoolbus',
  delivery_van: 'van',
  deliveryvan: 'van',
  tracktor: 'tractor',
  excavator: 'jcb',
  plane: 'car',
};

const ACCESSORY_TYPE_TO_ICON = {
  auto: { black: AutoBlack, blue: AutoBlueAcc, green: AutoGreenAcc, red: AutoRedAcc, yellow: AutoYellowAcc },
  ambulance: { black: AmbulanceBlack, blue: AmbulanceBlueAcc, green: AmbulanceGreenAcc, red: AmbulanceRedAcc, yellow: AmbulanceYellowAcc },
  bike: { black: BikeBlack, blue: BikeBlueAcc, green: BikeGreenAcc, red: BikeRedAcc, yellow: BikeYellowAcc },
  bus: { black: BusBlack, blue: BusBlueAcc, green: BusGreenAcc, red: BusRedAcc, yellow: BusYellowAcc },
car: {
  black: CarBlack,
  blue: CarBlueAcc,
  green: CarGreenAcc,
  purple: CarPurpleAcc,
  red: CarRedAcc,
  yellow: CarYellowAcc,
},
  crane: { black: CraneBlack, blue: CraneBlueAcc, green: CraneGreenAcc, red: CraneRedAcc, yellow: CraneYellowAcc },
  jcb: { black: JcbBlack, blue: JcbBlueAcc, green: JcbGreenAcc, red: JcbRedAcc, yellow: JcbYellowAcc },
  pickup: { black: PickupBlack, blue: PickupBlueAcc, green: PickupGreenAcc, red: PickupRedAcc, yellow: PickupYellowAcc },
  policecar: { black: PolicecarBlack, blue: PolicecarBlueAcc, green: PolicecarGreenAcc, red: PolicecarRedAcc, yellow: PolicecarYellowAcc },
  schoolbus: { black: SchoolbusBlack, blue: SchoolbusBlueAcc, green: SchoolbusGreenAcc, red: SchoolbusRedAcc, yellow: SchoolbusYellowAcc },
  tractor: { black: TractorBlack, blue: TractorBlueAcc, green: TractorGreenAcc, red: TractorRedAcc, yellow: TractorYellowAcc },
  truck: { black: TruckBlack, blue: TruckBlueAcc, green: TruckGreenAcc, red: TruckRedAcc, yellow: TruckYellowAcc },
  van: { black: VanBlack, blue: VanBlueAcc, green: VanGreenAcc, red: VanRedAcc, yellow: VanYellowAcc },
};

// Status → icon colour — must stay in sync with DeviceSidebar's STATUS_TO_ICON_COLOR
const statusToAccessoryColor = (status) => {
  const s = String(status || '').toLowerCase().trim();
  // ── running / moving ──
  if (s === 'running' || s === 'moving') return 'green';
  // ── idle / idling ──
  if (s === 'idle' || s === 'idling') return 'yellow';
  // ── stopped / parked ──
  if (s === 'stopped' || s === 'parked') return 'red';
  // ── inactive / expired / offline → blue ──
if (s === 'inactive' || s === 'offline') return 'blue';
if (s === 'expired') return 'purple';
  // ── no_data / nodata / unknown → black ──
  return 'black';
};

const resolveAccessoryIcon = (rawTypeOrIcon, status) => {
  const raw = String(rawTypeOrIcon || 'car').toLowerCase();
  const compact = raw.replace(/[^a-z0-9]/g, '');
  let typeKey = compact || 'car';
  typeKey = ACCESSORY_TYPE_ALIASES[typeKey] || typeKey;

  const typeIcons = ACCESSORY_TYPE_TO_ICON[typeKey];
  if (typeIcons) {
    return typeIcons[statusToAccessoryColor(status)] || typeIcons.black || CarBlack;
  }

  const fallback = ACCESSORY_TYPE_TO_ICON.car;
  return fallback[statusToAccessoryColor(status)] || fallback.black || CarBlack;
};

// 🎨 GEOFENCE STYLES
const GEOFENCE_STYLES = {
  fillColor: '#10b981',
  fillOpacity: 0.15,
  strokeColor: '#059669',
  strokeWeight: 2,
  strokeOpacity: 0.8,
};

const SELECTED_GEOFENCE_STYLES = {
  fillColor: '#3b82f6',
  fillOpacity: 0.25,
  strokeColor: '#2563eb',
  strokeWeight: 3,
  strokeOpacity: 1,
};

// 🚗 Professional icon sizing with stable road alignment on all zoom levels
const getIconSize = (zoom, status) => {
  const base = status === 'no_data' ? 22 : 28;
  
  if (zoom <= 4) return Math.round(base * 0.42);
  if (zoom <= 6) return Math.round(base * 0.5);
  if (zoom <= 8) return Math.round(base * 0.68);
  if (zoom <= 10) return Math.round(base * 0.84);
  if (zoom <= 12) return Math.round(base * 0.96);
  if (zoom <= 14) return Math.round(base * 1.08);
  if (zoom <= 16) return Math.round(base * 1.22);
  if (zoom <= 18) return Math.round(base * 1.42);
  
  return Math.round(base * 1.55);
};

const getClusterVehicleIconSize = (count) => {
  if (count >= 1000) return 42;
  if (count >= 250) return 40;
  if (count >= 100) return 38;
  if (count >= 25) return 36;
  return 34;
};

const createImperativeVehicleMarkerClass = () =>
  class ImperativeVehicleMarker extends window.google.maps.OverlayView {
    constructor({ markerId, onClick }) {
      super();
      this.markerId = markerId;
      this.onClick = onClick;
      this.position = null;
      this.visible = true;
      this.root = document.createElement('div');
      this.label = document.createElement('div');
      this.inner = document.createElement('div');
      this.img = document.createElement('img');

      this.root.style.position = 'absolute';
      this.root.style.cursor = 'pointer';
      this.root.style.transform = 'translate3d(0px, 0px, 0)';
      this.root.style.willChange = 'transform';
      this.root.style.pointerEvents = 'auto';
      this.root.style.width = '0';
      this.root.style.height = '0';
      this.root.style.overflow = 'visible';

      this.label.style.position = 'absolute';
      this.label.style.left = '50%';
      this.label.style.top = '0';
      this.label.style.transform = 'translate(-50%, -100%)';
      this.label.style.background = 'rgba(15, 23, 42, 0.92)';
      this.label.style.color = '#fff';
      this.label.style.borderRadius = '999px';
      this.label.style.padding = '4px 10px';
      this.label.style.fontSize = '11px';
      this.label.style.lineHeight = '1.2';
      this.label.style.fontWeight = '700';
      this.label.style.letterSpacing = '0.02em';
      this.label.style.whiteSpace = 'nowrap';
      this.label.style.maxWidth = '132px';
      this.label.style.overflow = 'hidden';
      this.label.style.textOverflow = 'ellipsis';
      this.label.style.boxShadow = '0 8px 18px rgba(15, 23, 42, 0.24)';
      this.label.style.pointerEvents = 'none';

      this.inner.style.position = 'absolute';
      this.inner.style.left = '50%';
      this.inner.style.top = '50%';
      this.inner.style.transform = 'translate(-50%, -50%)';
      this.inner.style.transformOrigin = 'center center';
      this.inner.style.transition = 'none';

      this.img.style.display = 'block';
      this.img.style.objectFit = 'contain';
      this.img.draggable = false;

      this.root.appendChild(this.label);
      this.inner.appendChild(this.img);
      this.root.appendChild(this.inner);
      this.root.addEventListener('click', (event) => {
        event.stopPropagation();
        if (typeof this.onClick === 'function') {
          this.onClick(this.markerId);
        }
      });
    }

    onAdd() {
      const panes = this.getPanes();
      panes?.overlayMouseTarget?.appendChild(this.root);
    }

    draw() {
      if (!this.root || !this.visible || !this.position) return;
      const projection = this.getProjection();
      if (!projection) return;
      const latLng = new window.google.maps.LatLng(this.position.lat, this.position.lng);
      const pixel = projection.fromLatLngToDivPixel(latLng);
      if (!pixel) return;
      const transform = `translate3d(${Number(pixel.x).toFixed(2)}px, ${Number(pixel.y).toFixed(2)}px, 0)`;
      if (this.currentTransform !== transform) {
        this.root.style.transform = transform;
        this.currentTransform = transform;
      }
    }

    onRemove() {
      this.root?.parentNode?.removeChild(this.root);
    }

    update({
      position,
      rotation,
      markerIcon,
      iconSize,
      isSelected,
      isPredicted,
      title,
      zIndex,
      onClick,
    }) {
      if (position && isValidGPS(Number(position.lat), Number(position.lng))) {
        this.position = { lat: Number(position.lat), lng: Number(position.lng) };
      }
      if (typeof onClick === 'function') {
        this.onClick = onClick;
      }

      const size = Number(iconSize || 24);

if (this.currentSize !== size) {
  this.inner.style.width = `${size}px`;
  this.inner.style.height = `${size}px`;
  this.label.style.marginTop = `${Math.max(6, Math.round(size * 0.28)) * -1}px`;
  this.currentSize = size;
}

const nextRotation = Number.isFinite(Number(rotation))
  ? Number(rotation).toFixed(2)
  : '0.00';

if (this.currentRotation !== nextRotation) {
  // Keep the icon centre anchored to the GPS pixel while it rotates. Replacing
  // this with only `rotate(...)` moves the icon's top-left corner to the
  // coordinate, which looks increasingly off-road as the map is zoomed out.
  this.inner.style.transform = `translate(-50%, -50%) rotate(${nextRotation}deg)`;
  this.currentRotation = nextRotation;
}


if (this.currentIcon !== markerIcon) {
  this.img.src = markerIcon;
  this.currentIcon = markerIcon;
}

const nextAlt = title || 'vehicle';
if (this.currentAlt !== nextAlt) {
  this.img.alt = nextAlt;
  this.currentAlt = nextAlt;
}

const nextLabel = String(title || '').trim();
if (this.currentLabel !== nextLabel) {
  this.label.textContent = nextLabel;
  this.label.style.display = nextLabel ? 'block' : 'none';
  this.currentLabel = nextLabel;
}

const nextFilter = isSelected
  ? 'drop-shadow(0 0 6px rgba(59, 130, 246, 0.7))'
  : 'none';

if (this.currentFilter !== nextFilter) {
  this.img.style.filter = nextFilter;
  this.currentFilter = nextFilter;
}

const nextOpacity = isPredicted ? '0.85' : '1';
if (this.currentOpacity !== nextOpacity) {
  this.img.style.opacity = nextOpacity;
  this.currentOpacity = nextOpacity;
}

this.draw();  
    }

    setMarkerVisible(visible) {
      this.visible = Boolean(visible);
      this.root.style.display = this.visible ? 'grid' : 'none';
      if (this.visible) {
        this.draw();
      }
    }
  };

const GeofenceRenderer = memo(({ geofence, isSelected, onClick }) => {
  const styles = isSelected ? SELECTED_GEOFENCE_STYLES : GEOFENCE_STYLES;

  if (geofence.type === 'circle') {
    return (
      <Circle
        center={geofence.center}
        radius={geofence.radius}
        options={{
          ...styles,
          clickable: true,
        }}
        onClick={onClick}
      />
    );
  }

  if (geofence.type === 'polygon' && geofence.polygon?.coordinates?.[0]) {
    const paths = geofence.polygon.coordinates[0].map(coord => ({
      lat: coord[1],
      lng: coord[0]
    }));

    return (
      <Polygon
        paths={paths}
        options={{
          ...styles,
          clickable: true,
        }}
        onClick={onClick}
      />
    );
  }

  return null;
});


const ServerClusterMarker = memo(({ cluster, onClick }) => {
  const count = Number(cluster?.count || 0);
  const lat = Number(cluster?.latitude ?? cluster?.lat);
  const lng = Number(cluster?.longitude ?? cluster?.lng);
  const representativeType =
    cluster?.iconCategory ||
    cluster?.vehicleType ||
    cluster?.deviceIcon ||
    cluster?.members?.[0]?.vehicle?.device?.vehicleType ||
    cluster?.members?.[0]?.vehicle?.device?.deviceIcon ||
    cluster?.members?.[0]?.vehicle?.device?.vehicleIcon ||
    cluster?.members?.[0]?.vehicle?.device?.icon ||
    cluster?.members?.[0]?.vehicleType ||
    "car";
  const iconSrc = resolveAccessoryIcon(representativeType, cluster?.status || "inactive");
  const iconSize = getClusterVehicleIconSize(count);

  if (!isValidMapCoordinate(lat, lng)) return null;

  return (
    <OverlayView
      position={{ lat, lng }}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <div
        onClick={onClick}
        style={{
          position: "relative",
          width: iconSize,
          height: iconSize,
          transform: "translate(-50%, -50%)",
          display: "grid",
          placeItems: "center",
          cursor: "pointer",
          userSelect: "none",
        }}
        title={`${count} vehicles`}
      >
        <img
          src={iconSrc}
          alt="vehicle cluster"
          draggable={false}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            filter: "drop-shadow(0 8px 14px rgba(15, 23, 42, 0.28))",
          }}
        />
        <div
          style={{
            position: "absolute",
            right: -8,
            bottom: -4,
            minWidth: count >= 1000 ? 34 : count >= 100 ? 30 : 24,
            height: 20,
            padding: "0 6px",
            borderRadius: 999,
            background: "#1d4ed8",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 800,
            border: "2px solid #fff",
            boxShadow: "0 8px 18px rgba(29, 78, 216, 0.28)",
            lineHeight: 1,
          }}
        >
          {count > 999 ? "999+" : count}
        </div>
      </div>
    </OverlayView>
  );
});


const MapContainer = ({
  map,
  setMap,
  mapType,
  showTraffic,
  devicesOnMap,
  clusterSourceDevices = [],
  selectedDevices,
  setSelectedDevices,
  handleDeviceClick,
  setFollowMode,
  followMode,
  routeStart,
  routeEnd,
  setRouteDetails,
  onDeviceClick,
  onEditDevice,
  onOpenGeofence,
  onShowDeviceHistory,
  onSendCommand,
  openMoreMenu,
  playbackData,
  isReplayMode = false,
  hiddenLiveMarkers = new Set(),
  showGeofences = true,
  sharedGeofences = [],
  landmarks = [],
  draftLandmark = null,
  landmarkFocusPoint = null,
  landmarkPlacementActive = false,
  onDraftLandmarkChange,
  selectedGeofence = null,
  selectedLandmark = null,
  onSelectLandmark,
  selectedNotification = null,
  onClearSelectedNotification,
  socketUpdatesRef,
  forceTripId = null,
  forceTripVehicleId = null,
  forceRoute = null,
  forceRoutes = null,
  tick = 0,
onViewportChange,
serverClusters = [],
mapMode = "cluster",
mapLoading = false,
mapTotal = 0,
resolvedAddressHint = "",
}) => {
  useDevRenderInstrumentation("MapContainer", () => ({
    devicesOnMapCount: Array.isArray(devicesOnMap) ? devicesOnMap.length : 0,
    selectedDevicesCount: Array.isArray(selectedDevices) ? selectedDevices.length : 0,
    mapType,
    followMode,
    mapMode,
    mapLoading,
    serverClustersCount: Array.isArray(serverClusters) ? serverClusters.length : 0,
  }));

  const [directionsResponse, setDirectionsResponse] = useState(null);
  const finishMapContainerProfile = beginProfile("MapContainer", { stage: "component" });
  const mapContainerProfileRef = useRef(null);
  const mapRef = useRef(null);
  const isPanningRef = useRef(false);
  const userInteractedRef = useRef(false);
  const followPanRafRef = useRef(null);
  const followResumeTimerRef = useRef(null);
  const followPauseUntilRef = useRef(0);
  const latestFollowTargetRef = useRef(null);
  const followWasMovingRef = useRef(false);
  const [mapZoom, setMapZoom] = useState(2);
  const { on } = useSocket();
  const [markerPopupDeviceId, setMarkerPopupDeviceId] = useState(null);
  const [markerPopupAddress, setMarkerPopupAddress] = useState("");
  const markerPopupGeocodeRef = useRef({ key: "", lat: null, lng: null, at: 0 });
  const previewCircleRef = useRef(null);
  const lastPreviewFocusKeyRef = useRef("");
  const trackingStateRef = useRef(new Map());
  const liveVisualStateRef = useRef(new Map());
  const markersRef = useRef({});
  const markerClassRef = useRef(null);
  const markerSyncRafRef = useRef(null);
  const displayedVehiclesRef = useRef([]);
  const renderedVehiclesRef = useRef([]);
  const processedVehicleCacheRef = useRef(new Map());
  const displayedVehiclesLookupRef = useRef(new Map());
  const processedVehiclesRef = useRef([]);
  const didInitialViewportFitRef = useRef(false);
  const devicesLookupRef = useRef(new Map());
  const devicesOnMapRef = useRef(devicesOnMap);
  const selectedDevicesRef = useRef(selectedDevices);
  const markerPopupDeviceIdRef = useRef(markerPopupDeviceId);
  const handleVehicleMarkerClickRef = useRef(null);
  const focusedVehicleLivePosMetaRef = useRef({ markerId: null, lat: null, lng: null, at: 0 });
  const liveVisualRafRef = useRef(null);
  const liveVisualFrameAtRef = useRef(0);
  const [viewportSnapshot, setViewportSnapshot] = useState(null);
  const [focusedVehicleLivePos, setFocusedVehicleLivePos] = useState(null);
  const { data: tripsData = [] } = useGetTripsQuery(undefined, {
    pollingInterval: 30000,
    refetchOnFocus: true,
  });

  const syncVisualStateToPoint = useCallback((state, point, options = {}) => {
    if (!state || !point) return false;

    const lat = Number(point.lat);
    const lng = Number(point.lng);
    if (!isValidGPS(lat, lng)) return false;

    const heading = normalizeDeg(
      options.heading ??
      state.targetHeading ??
      state.currentHeading ??
      state.lastHeading ??
      0
    );
    const renderHeading = normalizeDeg(options.renderHeading ?? heading);
    const timestamp = Number.isFinite(Number(options.timestamp))
      ? Number(options.timestamp)
      : Date.now();

    state.currentLat = lat;
    state.currentLng = lng;
    state.targetLat = lat;
    state.targetLng = lng;
    state.currentHeading = heading;
    state.targetHeading = heading;
    state.renderHeading = renderHeading;
    state.targetRenderHeading = renderHeading;
    state.lastLat = lat;
    state.lastLng = lng;
    state.lastHeading = heading;
    state.prev = { lat, lng, timestamp, heading };
    state.next = { lat, lng, timestamp, heading };
    state.animStart = 0;
    state.animDuration = 0;
    state.turnPivot = null;
    state.headingFrom = heading;
    state.headingTo = heading;
    state.segmentFromLat = lat;
    state.segmentFromLng = lng;
    state.segmentToLat = lat;
    state.segmentToLng = lng;
    state.segmentStartAt = 0;
    state.segmentDurationMs = 0;
    state.visualPacketTs = timestamp;
    state.isAnimating = false;

    if (options.clearQueue !== false) {
      state.movementQueue = [];
    }

    return true;
  }, []);

  const queueVisualMove = useCallback((state, nextMove, options = {}) => {
    if (!state || !nextMove) return false;
    if (!Array.isArray(state.movementQueue)) {
      state.movementQueue = [];
    }

    const packetTs = Number(nextMove.packetTs || 0);
    const queue = state.movementQueue;
    const tail = queue[queue.length - 1];
    const shouldReplaceTail =
      Boolean(options.replaceTail) &&
      tail &&
      Number(tail.packetTs || 0) <= packetTs;

    if (shouldReplaceTail) {
      queue[queue.length - 1] = nextMove;
    } else {
      queue.push(nextMove);
    }

    return true;
  }, []);


  const scheduleFollowResume = useCallback(() => {
    if (!followMode) return;
    if (followResumeTimerRef.current) {
      clearTimeout(followResumeTimerRef.current);
    }
    followResumeTimerRef.current = setTimeout(() => {
      followResumeTimerRef.current = null;
      followPauseUntilRef.current = 0;
      const target = latestFollowTargetRef.current;
      if (!target || !mapRef.current || !followMode) return;
      mapRef.current.panTo(target);
    }, 3000);
  }, [followMode]);

  const onZoomChanged = useCallback(() => {
    if (!map) return;
    const zoom = map.getZoom();
    setMapZoom(zoom);
    userInteractedRef.current = true;
    if (!followMode) return;
    followPauseUntilRef.current = Date.now() + 3000;
    scheduleFollowResume();
  }, [followMode, map, scheduleFollowResume]);

  const publishViewportSnapshot = useCallback((activeMap = mapRef.current) => {
    const snapshot = getMapViewportSnapshot(activeMap);
    if (!snapshot?.bounds) return;
    setViewportSnapshot(snapshot);
    onViewportChange?.(snapshot);
  }, [onViewportChange]);

  const selectedNotificationPosition = useMemo(() => {
    const lat = Number(
      selectedNotification?.location?.latitude ??
      selectedNotification?.location?.lat ??
      selectedNotification?.latitude ??
      selectedNotification?.lat
    );
    const lng = Number(
      selectedNotification?.location?.longitude ??
      selectedNotification?.location?.lng ??
      selectedNotification?.longitude ??
      selectedNotification?.lng
    );
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }, [selectedNotification]);

  const allGeofences = useMemo(() => {
    const geofencesById = new Map();
    devicesOnMap.forEach(device => {
      if (device.geofences && Array.isArray(device.geofences)) {
        device.geofences.forEach((geofence, index) => {
          if (geofence.isActive !== false) {
            const geofenceId =
              geofence?.sourceGeofenceId ||
              geofence?._id ||
              geofence?.id ||
              `${device._id}-${geofence?.name || 'geofence'}-${index}`;
            const existing = geofencesById.get(String(geofenceId));
            const next = {
              ...geofence,
              geofenceId,
              deviceId: existing?.deviceId || device._id,
              deviceName: existing?.deviceName || device.name,
              assignedDeviceIds: [
                ...(Array.isArray(existing?.assignedDeviceIds) ? existing.assignedDeviceIds : []),
                device._id,
              ],
              isSelected:
                String(selectedGeofence?.id || "") === String(geofenceId) ||
                selectedDevices.includes(device._id)
            };
            geofencesById.set(String(geofenceId), next);
          }
        });
      }
    });

    (Array.isArray(sharedGeofences) ? sharedGeofences : []).forEach((geofence, index) => {
      if (geofence?.isActive === false) return;
      const geofenceId = String(geofence?._id || geofence?.id || `shared-geofence-${index}`);
      const existing = geofencesById.get(geofenceId) || {};
      geofencesById.set(geofenceId, {
        ...existing,
        ...geofence,
        geofenceId,
        deviceId: existing?.deviceId || null,
        deviceName: existing?.deviceName || geofence?.name || "Geofence",
        assignedDeviceIds: Array.isArray(existing?.assignedDeviceIds) ? existing.assignedDeviceIds : [],
        isSelected: String(selectedGeofence?.id || "") === geofenceId || existing?.isSelected === true,
      });
    });

    return [...geofencesById.values()];
  }, [devicesOnMap, selectedDevices, selectedGeofence, sharedGeofences]);

  const allLandmarks = useMemo(() => {
    return (Array.isArray(landmarks) ? landmarks : [])
      .map((landmark, index) => {
        const lat = Number(landmark?.latitude ?? landmark?.lat);
        const lng = Number(landmark?.longitude ?? landmark?.lng);
        if (!isValidGPS(lat, lng)) return null;
        return {
          ...landmark,
          landmarkId: String(landmark?._id || landmark?.id || `landmark-${index}`),
          lat,
          lng,
          radius: Number(landmark?.radius ?? landmark?.radiusMeters ?? 30),
          isSelected: String(selectedLandmark?.id || "") === String(landmark?._id || landmark?.id || `landmark-${index}`),
        };
      })
      .filter(Boolean);
  }, [landmarks, selectedLandmark]);

  const previewLandmark = useMemo(() => {
    const lat = Number(draftLandmark?.latitude ?? draftLandmark?.lat);
    const lng = Number(draftLandmark?.longitude ?? draftLandmark?.lng);
    if (!isValidGPS(lat, lng)) return null;
    return {
      ...draftLandmark,
      lat,
      lng,
      radius: Number(draftLandmark?.radius ?? draftLandmark?.radiusMeters ?? 30),
    };
  }, [draftLandmark]);

  const syncPreviewCircleToDraft = useCallback(() => {
    const circle = previewCircleRef.current;
    if (!circle || typeof onDraftLandmarkChange !== "function") return;
    const center = circle.getCenter?.();
    const lat = center?.lat?.();
    const lng = center?.lng?.();
    const radius = circle.getRadius?.();
    if (!isValidGPS(lat, lng)) return;

    onDraftLandmarkChange((current) => {
      const nextRadius = Math.max(0, Number(radius || current?.radius || 30));
      const currentLat = Number(current?.latitude ?? current?.lat);
      const currentLng = Number(current?.longitude ?? current?.lng);
      const currentRadius = Number(current?.radius ?? current?.radiusMeters ?? 30);
      const unchanged =
        Math.abs(currentLat - lat) < 0.000001 &&
        Math.abs(currentLng - lng) < 0.000001 &&
        Math.abs(currentRadius - nextRadius) < 0.5;
      if (unchanged) return current;
      return {
        ...(current || {}),
        latitude: lat,
        longitude: lng,
        lat,
        lng,
        radius: nextRadius,
      };
    });
  }, [onDraftLandmarkChange]);

  const handleLandmarkCircleComplete = useCallback((circle) => {
    if (!circle || typeof onDraftLandmarkChange !== "function") return;
    const center = circle.getCenter?.();
    const lat = center?.lat?.();
    const lng = center?.lng?.();
    const radius = circle.getRadius?.();
    if (!isValidGPS(lat, lng)) {
      circle.setMap?.(null);
      return;
    }

    onDraftLandmarkChange((current) => ({
      ...(current || {}),
      latitude: lat,
      longitude: lng,
      lat,
      lng,
      radius: Math.max(0, Number(radius || current?.radius || 30)),
    }));
    circle.setMap?.(null);
  }, [onDraftLandmarkChange]);

useEffect(() => {
  if (!map || !selectedNotificationPosition) return;
  map.panTo(selectedNotificationPosition);
  if ((map.getZoom?.() || 0) < 16) {
    map.setZoom(16);
  }
}, [map, selectedNotificationPosition]);

useEffect(() => {
  if (!map || !selectedLandmark) return;
  const lat = Number(selectedLandmark?.latitude ?? selectedLandmark?.lat);
  const lng = Number(selectedLandmark?.longitude ?? selectedLandmark?.lng);
  if (!isValidGPS(lat, lng)) return;
  map.panTo({ lat, lng });
  if ((map.getZoom?.() || 0) < 16) {
    map.setZoom(16);
  }
}, [map, selectedLandmark]);

useEffect(() => {
  if (!map || previewLandmark || !landmarkFocusPoint) return;
  const lat = Number(landmarkFocusPoint?.latitude ?? landmarkFocusPoint?.lat);
  const lng = Number(landmarkFocusPoint?.longitude ?? landmarkFocusPoint?.lng);
  if (!isValidGPS(lat, lng)) return;
  const focusKey = [Number(lat).toFixed(6), Number(lng).toFixed(6), "focus"].join(":");
  if (lastPreviewFocusKeyRef.current === focusKey) return;
  lastPreviewFocusKeyRef.current = focusKey;
  map.panTo({ lat, lng });
  if ((map.getZoom?.() || 0) < 16) {
    map.setZoom(16);
  }
}, [landmarkFocusPoint, map, previewLandmark]);

useEffect(() => {
  const next = new Map();
  devicesOnMap.forEach((device) => {
    if (device?._id) next.set(String(device._id), device);
    if (device?.imei) next.set(String(device.imei), device);
  });
  devicesLookupRef.current = next;
}, [devicesOnMap]);

useEffect(() => {
  devicesOnMapRef.current = devicesOnMap;
}, [devicesOnMap]);

useEffect(() => {
  selectedDevicesRef.current = selectedDevices;
}, [selectedDevices]);

useEffect(() => {
  if (!selectedDevices.length) {
    didInitialViewportFitRef.current = false;
  }
}, [selectedDevices]);

useEffect(() => {
  markerPopupDeviceIdRef.current = markerPopupDeviceId;
}, [markerPopupDeviceId]);

const getCurrentMarkerPosition = useCallback((markerId, fallbackPos = null) => {
  const visual = markerId ? liveVisualStateRef.current.get(String(markerId)) : null;
  const lat = Number(visual?.currentLat ?? visual?.targetLat ?? fallbackPos?.lat);
  const lng = Number(visual?.currentLng ?? visual?.targetLng ?? fallbackPos?.lng);
  if (!isValidGPS(lat, lng)) return null;
  return { lat, lng };
}, []);

const getCurrentMarkerRotation = useCallback((markerId, fallbackRotation = 0) => {
  const visual = markerId ? liveVisualStateRef.current.get(String(markerId)) : null;
  const heading = Number(
    visual?.isAnimating
      ? visual?.currentHeading
      : visual?.renderHeading ?? fallbackRotation
  );
  return Number.isFinite(heading) ? normalizeDeg(heading) : 0;
}, []);

const isVehicleWithinViewport = useCallback((position) => {
  const activeMap = mapRef.current;
  if (!activeMap || !position || !window.google?.maps) return true;
  const bounds = activeMap.getBounds?.();
  if (!bounds) return true;

  const northEast = bounds.getNorthEast?.();
  const southWest = bounds.getSouthWest?.();
  if (!northEast || !southWest) return true;

  const lat = Number(position.lat);
  const lng = Number(position.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;

  const latPad = Math.max((northEast.lat() - southWest.lat()) * 0.2, 0.02);
  const lngPad = Math.max((northEast.lng() - southWest.lng()) * 0.2, 0.02);
  const minLat = southWest.lat() - latPad;
  const maxLat = northEast.lat() + latPad;
  const minLng = southWest.lng() - lngPad;
  const maxLng = northEast.lng() + lngPad;

  return lat >= minLat && lat <= maxLat && lng >= minLng && lng <= maxLng;
}, []);

const clearImperativeMarkers = useCallback(() => {
  Object.values(markersRef.current).forEach((marker) => {
    marker?.setMap?.(null);
  });
  markersRef.current = {};
}, []);

const syncFocusedVehicleLivePosition = useCallback((force = false) => {
  const markerId = String(markerPopupDeviceIdRef.current || selectedDevicesRef.current?.[0] || "");
  if (!markerId) {
    focusedVehicleLivePosMetaRef.current = { markerId: null, lat: null, lng: null, at: 0 };
    setFocusedVehicleLivePos(null);
    return;
  }

  const fallbackVehicle = displayedVehiclesLookupRef.current.get(markerId) || processedVehiclesRef.current.find(
    (vehicle) => String(vehicle?.device?._id || vehicle?.device?.imei || "") === markerId
  );
  const position = getCurrentMarkerPosition(markerId, fallbackVehicle?.pos);
  if (!position) return;

  const now = Date.now();
  const prev = focusedVehicleLivePosMetaRef.current;
  const moved = haversineMeters(
    prev && isValidGPS(prev.lat, prev.lng) ? { lat: prev.lat, lng: prev.lng } : null,
    position
  );
  if (!force && prev.markerId === markerId && moved < 1 && now - prev.at < 150) {
    return;
  }

  focusedVehicleLivePosMetaRef.current = {
    markerId,
    lat: position.lat,
    lng: position.lng,
    at: now,
  };
  setFocusedVehicleLivePos({
    markerId,
    lat: position.lat,
    lng: position.lng,
    speed: Number(fallbackVehicle?.speed || 0),
    status: String(fallbackVehicle?.status || ""),
    ts: Number(fallbackVehicle?.packetTs || 0),
  });
}, [getCurrentMarkerPosition]);

const handleImperativeMarkerClick = useCallback((markerId) => {
  const resolvedId = String(markerId || "");
  if (!resolvedId) return;
  const vehicle =
    displayedVehiclesLookupRef.current.get(resolvedId) ||
    processedVehiclesRef.current.find(
      (item) => String(item?.device?._id || item?.device?.imei || "") === resolvedId
    );
  const device = vehicle?.device || devicesLookupRef.current.get(resolvedId) || null;
  if (device) {
    handleVehicleMarkerClickRef.current?.(device);
  }
}, []);

const getMarkerAsset = useCallback((device, status) => {
  return resolveAccessoryIcon(
    device?.vehicleType ?? device?.deviceIcon ?? device?.vehicleIcon ?? device?.icon,
    status
  );
}, []);

const getMarkerClass = useCallback(() => {
  if (markerClassRef.current || !window.google?.maps?.OverlayView) {
    return markerClassRef.current;
  }
  markerClassRef.current = createImperativeVehicleMarkerClass();
  return markerClassRef.current;
}, []);

const syncImperativeMarkers = useCallback(() => {
  const activeMap = mapRef.current;

  const shouldHideImperativeMarkers = false;

  if (shouldHideImperativeMarkers) {
    clearImperativeMarkers();
    return;
  }

  if (!activeMap || !window.google?.maps) return;

  const MarkerClass = getMarkerClass();
  if (!MarkerClass) return;

  const activeMarkerIds = new Set();
  // The live-map sidebar supplies a progressively loaded page of devices.  A
  // small loaded page must remain visible even at the default/world zoom; the
  // previous zoom guard made those markers appear only after a checkbox click
  // (selected markers bypassed the guard).  Keep the guard for large datasets
  // so the existing cluster/performance behaviour is unchanged.
  const isLoadedVehicleBatch =
    renderedVehiclesRef.current.length > 0 && renderedVehiclesRef.current.length <= 250;
  const shouldRenderMarkers = mapZoom >= 6 || isLoadedVehicleBatch;
  const MAX_VISIBLE_MARKERS = WEB_MAP_MAX_VISIBLE_MARKERS;
  let visibleMarkerCount = 0;

renderedVehiclesRef.current.forEach((vehicle) => {

    const markerId = String(vehicle?.device?._id || vehicle?.device?.imei || "");
if (!markerId) return;

if (!shouldRenderMarkers && !vehicle.isSelected) {
  const existingMarker = markersRef.current[markerId];
  if (existingMarker?.getMap?.()) existingMarker.setMap(null);
  return;
}

if (!vehicle.isSelected && visibleMarkerCount >= MAX_VISIBLE_MARKERS) {
  const existingMarker = markersRef.current[markerId];
  if (existingMarker?.getMap?.()) existingMarker.setMap(null);
  return;
}

const position = getCurrentMarkerPosition(markerId, vehicle.pos);


    if (!position) return;

    activeMarkerIds.add(markerId);
if (!vehicle.isSelected) visibleMarkerCount++;

let marker = markersRef.current[markerId];

    if (!marker) {
      marker = new MarkerClass({
        markerId,
        onClick: handleImperativeMarkerClick,
      });
      markersRef.current[markerId] = marker;
    }

    const visibleInViewport =
      vehicle.isSelected || isLoadedVehicleBatch || isVehicleWithinViewport(position);
    const markerIcon = getMarkerAsset(vehicle.device, vehicle.status);
    marker.update({
      position,
      rotation: getCurrentMarkerRotation(markerId, vehicle.rotation),
      markerIcon,
      iconSize: getIconSize(mapZoom, vehicle.status),
      isSelected: vehicle.isSelected,
      isPredicted: false,
      title: vehicle?.device?.name || vehicle?.device?.imei || 'Vehicle',
      zIndex: vehicle.isSelected ? 1000 : 10,
      onClick: handleImperativeMarkerClick,
    });
    logLiveVisualDebug("marker-render", () => ({
      markerId,
      renderSource: "liveVisualState.currentLatLng",
      rotation: getCurrentMarkerRotation(markerId, vehicle.rotation),
      position: {
        lat: Number(position.lat).toFixed(6),
        lng: Number(position.lng).toFixed(6),
      },
      fallbackPacketPosition: {
        lat: Number(vehicle?.pos?.lat).toFixed(6),
        lng: Number(vehicle?.pos?.lng).toFixed(6),
      },
      isSelected: vehicle.isSelected,
    }));

    if (visibleInViewport) {
      if (!marker.getMap?.()) {
        marker.setMap(activeMap);
      }
      marker.setMarkerVisible(true);
    } else if (marker.getMap?.()) {
      marker.setMap(null);
    }
  });

  Object.keys(markersRef.current).forEach((markerId) => {
    if (activeMarkerIds.has(markerId)) return;
    markersRef.current[markerId]?.setMap?.(null);
    delete markersRef.current[markerId];
  });
}, [
  mapMode,
  serverClusters,
  clearImperativeMarkers,
  getCurrentMarkerPosition,
  getCurrentMarkerRotation,
  getMarkerAsset,
  getMarkerClass,
  handleImperativeMarkerClick,
  isVehicleWithinViewport,
  mapZoom,
]);


mapContainerProfileRef.current = {
  props: {
    devicesOnMapCount: Array.isArray(devicesOnMap) ? devicesOnMap.length : 0,
    selectedDevicesCount: Array.isArray(selectedDevices) ? selectedDevices.length : 0,
    serverClustersCount: Array.isArray(serverClusters) ? serverClusters.length : 0,
    mapMode,
    mapLoading,
  },
  state: {
    tick,
    markerPopupDeviceId,
  },
  deviceCount: Array.isArray(devicesOnMap) ? devicesOnMap.length : 0,
};

useEffect(() => {
  if (mapContainerProfileRef.current) {
    finishMapContainerProfile(mapContainerProfileRef.current);
  }
});

const scheduleImperativeMarkerSync = useCallback(() => {
  if (markerSyncRafRef.current) return;
  markerSyncRafRef.current = requestAnimationFrame(() => {
    markerSyncRafRef.current = null;
    syncImperativeMarkers();
  });
}, [syncImperativeMarkers]);

const processedVehicles = useMemo(() => {
  const selectedIds = new Set(
    (Array.isArray(selectedDevices) ? selectedDevices : [])
      .map((deviceId) => String(deviceId || ""))
      .filter(Boolean)
  );
  const cache = processedVehicleCacheRef.current;
  const nextCache = new Map();

  const processed = (Array.isArray(devicesOnMap) ? devicesOnMap : [])
    .slice(0, WEB_MAP_MAX_VISIBLE_MARKERS)
    .map((device) => {
      try {
        const deviceId = String(device?._id || device?.imei || "");
        if (!deviceId) return null;

        const visualState = liveVisualStateRef.current.get(deviceId);
        const rawSocketData =
          socketUpdatesRef?.current?.[device?._id] ||
          socketUpdatesRef?.current?.[device?.imei] ||
          {};
        const deviceResolvedStatus = resolveUiDeviceStatus(device).status;
        const isExpiredVehicle = deviceResolvedStatus === "expired";
        const socketData = isExpiredVehicle ? {} : rawSocketData;
        const liveDataFromApi = device.deviceLive || device.liveSnapshot || {};
        const dbData = {
          latitude: pickFinite(
            liveDataFromApi.latitude,
            liveDataFromApi.lat,
            device.currentLocation?.latitude,
            device.currentLocation?.lat,
            device.liveData?.latitude,
            device.liveData?.lat,
            device.geoLocation?.coordinates?.[1],
            device.location?.latitude,
            device.location?.lat,
            device.latitude,
            device.lat
          ),
          longitude: pickFinite(
            liveDataFromApi.longitude,
            liveDataFromApi.lng,
            device.currentLocation?.longitude,
            device.currentLocation?.lng,
            device.liveData?.longitude,
            device.liveData?.lng,
            device.geoLocation?.coordinates?.[0],
            device.location?.longitude,
            device.location?.lng,
            device.longitude,
            device.lng
          ),
          speed: pickFinite(
            liveDataFromApi.speed,
            device.liveData?.speed,
            device.currentLocation?.speed
          ) ?? 0,
          heading: pickFinite(
            liveDataFromApi.heading,
            liveDataFromApi.course,
            liveDataFromApi.bearing,
            device.liveData?.heading,
            device.liveData?.course,
            device.liveData?.bearing,
            device.currentLocation?.heading
          ) ?? 0,
          movementState:
            liveDataFromApi.movementState ??
            liveDataFromApi.movement_state ??
            device.liveData?.movementState ??
            device.currentLocation?.movementState ??
            null,
          direction:
            liveDataFromApi.direction ??
            device.liveData?.direction ??
            device.currentLocation?.direction ??
            null,
          turnType:
            liveDataFromApi.turnType ??
            liveDataFromApi.turn_type ??
            device.liveData?.turnType ??
            device.currentLocation?.turnType ??
            null,
          accStatus:
            liveDataFromApi.accStatus ??
            device.liveData?.accStatus ??
            device.currentLocation?.accStatus ??
            false,
          vehicleStatus:
            liveDataFromApi.vehicleStatus ??
            device.liveData?.vehicleStatus ??
            device.vehicleStatus ??
            "nodata",
          timestamp:
            liveDataFromApi.timestamp ??
            device.liveData?.timestamp ??
            device.currentLocation?.timestamp ??
            device.lastDataReceivedAt ??
            null,
        };

        const socketTs = toTimestampMs(
          socketData?.packetReceivedAt ??
            socketData?.lastSocketUpdate ??
            socketData?.timestamp ??
            socketData?.packetTime ??
            socketData?.serverTime
        );
        const dbTs = toTimestampMs(dbData?.timestamp);
        const hasSocketPayload = socketData && Object.keys(socketData).length > 0;
        const socketAgeMs = socketTs > 0 ? Date.now() - socketTs : Infinity;
        const socketFresh = Number.isFinite(socketAgeMs) && socketAgeMs <= 30000;
        const socketLocation = getSocketLocationMeta(socketData);
        const socketHasRenderableCoords = socketLocation.hasRenderableCoords;
        const useSocketData =
          hasSocketPayload &&
          (socketHasRenderableCoords || socketFresh || (socketTs > 0 && socketTs + 20000 >= dbTs));
        const data = useSocketData
          ? {
              ...dbData,
              ...socketData,
              latitude: socketHasRenderableCoords ? socketLocation.latitude : dbData.latitude,
              longitude: socketHasRenderableCoords ? socketLocation.longitude : dbData.longitude,
              lat: socketHasRenderableCoords ? socketLocation.latitude : dbData.latitude,
              lng: socketHasRenderableCoords ? socketLocation.longitude : dbData.longitude,
            }
          : dbData;

        const snapshot = getResolvedLiveSnapshot(device, socketData);
        const lat = pickFinite(
          snapshot.latitude,
          data?.latitude,
          data?.lat,
          dbData?.latitude,
          visualState?.targetLat,
          visualState?.currentLat
        );
        const lng = pickFinite(
          snapshot.longitude,
          data?.longitude,
          data?.lng,
          dbData?.longitude,
          visualState?.targetLng,
          visualState?.currentLng
        );
        const speed = isExpiredVehicle ? 0 : Number((snapshot.speed ?? data?.speed) || 0);
        const rawHeading = pickFinite(data?.heading, data?.course, data?.bearing);
        const statePacketTs = toTimestampMs(
          data?.packetReceivedAt ??
            data?.serverReceivedAt ??
            data?.receivedAt ??
            data?.lastSocketUpdate ??
            data?.timestamp ??
            data?.packetTime ??
            dbData?.timestamp ??
            device.lastDataReceivedAt
        );
        const packetTs = socketHasRenderableCoords
          ? statePacketTs || (useSocketData ? socketTs || Date.now() : dbTs || 0)
          : dbTs || 0;
        const uiStatus = resolveUiDeviceStatus(device, [socketData]);
        const canonicalStatus = isExpiredVehicle ? "expired" : uiStatus.status;
        const resolvedStatus = canonicalStatus;
        const isSelected = selectedIds.has(deviceId);
        const signature = JSON.stringify([
          isSelected,
          device?.isWiredGPS || false,
          resolvedStatus,
          canonicalStatus,
          packetTs || 0,
          statePacketTs || 0,
          socketTs || 0,
          dbTs || 0,
          socketHasRenderableCoords ? 1 : 0,
          useSocketData ? 1 : 0,
          lat,
          lng,
          speed,
          rawHeading,
          snapshot.accStatus ?? data?.accStatus ?? data?.acc ?? data?.ignition ?? null,
          data?.movementState ?? data?.movement_state ?? null,
          data?.direction ?? null,
          data?.turnType ?? data?.turn_type ?? null,
        ]);
        const cachedEntry = cache.get(deviceId);

        if (cachedEntry?.signature === signature) {
          nextCache.set(deviceId, cachedEntry);
          return cachedEntry.value;
        }

        if (!isValidGPS(lat, lng)) {
          nextCache.set(deviceId, { signature, value: null });
          return null;
        }

        const prevTracking = trackingStateRef.current.get(deviceId) || null;
        const tracking = resolveTrackingState({
          prev: prevTracking,
          speed,
          accStatus: isExpiredVehicle
            ? false
            : snapshot.accStatus ?? data?.accStatus ?? data?.acc ?? data?.ignition ?? null,
          timestampMs: statePacketTs || Date.now(),
          latitude: lat,
          longitude: lng,
          heading: rawHeading,
          vehicleStatus:
            canonicalStatus ??
            data?.vehicleStatus ??
            data?.vehicle_status ??
            data?.status ??
            resolvedStatus,
          movementState: data?.movementState ?? data?.movement_state ?? null,
          direction: data?.direction ?? null,
          turnType: data?.turnType ?? data?.turn_type ?? null,
        });
        trackingStateRef.current.set(deviceId, tracking);

        const nextValue = {
          device,
          pos: { lat: Number(lat), lng: Number(lng) },
          rawPos: { lat: Number(lat), lng: Number(lng) },
          rotation: normalizeDeg(tracking.heading),
          rawHeading: normalizeDeg(tracking.heading),
          movementState: tracking.movementState,
          direction: tracking.direction,
          turnType: tracking.turnType,
          status: resolvedStatus,
          isSelected,
          isWired: device.isWiredGPS || false,
          speed,
          accStatus: isExpiredVehicle ? false : tracking.accStatus ?? false,
          dataAge: null,
          dataSource: isExpiredVehicle ? "db" : useSocketData ? "socket" : "db",
          packetTs,
          roadSnapped: false,
          roadSnapAgeMs: Infinity,
        };

        nextCache.set(deviceId, { signature, value: nextValue });
        return nextValue;
      } catch (e) {
        console.warn(`Error processing vehicle ${device?._id}:`, e);
        return null;
      }
    })
    .filter(Boolean);

  processedVehicleCacheRef.current = nextCache;
  return processed;
}, [
  devicesOnMap, 
  selectedDevices,
  socketUpdatesRef,
  tick,
]);

  const ensureLiveVisualState = useCallback((markerId, vehicle, now = Date.now()) => {
    const targetLat = Number(vehicle?.pos?.lat);
    const targetLng = Number(vehicle?.pos?.lng);
    if (!markerId || !isValidGPS(targetLat, targetLng)) return null;

    let existing = liveVisualStateRef.current.get(markerId);
    if (existing) return existing;

    const targetHeading = normalizeDeg(vehicle?.rotation ?? vehicle?.rawHeading ?? 0);
    existing = {
      markerId,
      currentLat: targetLat,
      currentLng: targetLng,
      prev: { lat: targetLat, lng: targetLng, timestamp: now, heading: targetHeading },
      next: { lat: targetLat, lng: targetLng, timestamp: now, heading: targetHeading },
      animStart: 0,
      animDuration: 0,
      turnPivot: null,
      lastLat: targetLat,
      lastLng: targetLng,
      targetLat,
      targetLng,
      currentHeading: targetHeading,
      renderHeading: targetHeading,
      targetRenderHeading: targetHeading,
      lastHeading: targetHeading,
      targetHeading,
      headingFrom: targetHeading,
      headingTo: targetHeading,
      segmentFromLat: targetLat,
      segmentFromLng: targetLng,
      segmentToLat: targetLat,
      segmentToLng: targetLng,
      segmentStartAt: 0,
      segmentDurationMs: 0,
      packetArrivedAt: now,
      lastUpdateTime: now,
      lastPacketTs: Number.isFinite(Number(vehicle?.packetTs)) ? Number(vehicle.packetTs) : now,
      visualPacketTs: Number.isFinite(Number(vehicle?.packetTs)) ? Number(vehicle.packetTs) : now,
      lastDataSource: vehicle?.dataSource || 'unknown',
      speed: resolveLiveSpeed(vehicle?.speed, 0),
      lastSpeed: resolveLiveSpeed(vehicle?.speed, 0),
      accStatus: vehicle?.accStatus ?? null,
      status: vehicle?.status || "no_data",
      movementState: vehicle?.movementState || null,
      direction: vehicle?.direction || null,
      turnType: vehicle?.turnType || null,
      roadSnapped: Boolean(vehicle?.roadSnapped),
      roadSnapAgeMs: Number(vehicle?.roadSnapAgeMs ?? Infinity),
      movementQueue: [],
      isAnimating: false,
      lastPacketArrivalAt: now,
      expectedPacketGapMs: LIVE_VISUAL_EXPECTED_GAP_MIN_MS,
    };
    liveVisualStateRef.current.set(markerId, existing);
    return existing;
  }, []);

  const startNextQueuedVisualSegment = useCallback((state, now = Date.now()) => {
    if (!state) return false;

    while (Array.isArray(state.movementQueue) && state.movementQueue.length > 0) {
      const nextMove = state.movementQueue.shift();
      if (!nextMove) continue;

      const targetLat = Number(nextMove.lat);
      const targetLng = Number(nextMove.lng);
      if (!isValidGPS(targetLat, targetLng)) {
        continue;
      }

      const targetHeading = normalizeDeg(nextMove.heading ?? state.targetHeading ?? state.currentHeading ?? 0);
      const currentLat = Number(state.currentLat);
      const currentLng = Number(state.currentLng);
      const currentPosValid = isValidGPS(currentLat, currentLng);

      if (!currentPosValid) {
        syncVisualStateToPoint(state, { lat: targetLat, lng: targetLng }, {
          heading: targetHeading,
          timestamp: nextMove.packetTs ?? now,
        });
        continue;
      }

      const movedM = haversineMeters(
        { lat: currentLat, lng: currentLng },
        { lat: targetLat, lng: targetLng }
      );
      const turnHint =
        Boolean(String(nextMove.turnType || state.turnType || "").trim()) ||
        Boolean(String(nextMove.direction || state.direction || "").trim()) ||
        String(nextMove.movementState || state.movementState || "").toLowerCase().includes("turn");
      const speedNow = resolveLiveSpeed(nextMove.speed, state.lastSpeed ?? state.speed ?? 0);
      const minMoveAcceptM = turnHint ? 0.03 : speedNow > 0 ? 0.08 : 0.2;

      if (movedM <= minMoveAcceptM || movedM > 2500) {
        syncVisualStateToPoint(state, { lat: targetLat, lng: targetLng }, {
          heading: targetHeading,
          timestamp: nextMove.packetTs ?? now,
        });
        continue;
      }

      const previousVisualPacketTs = Number(state.visualPacketTs || 0);
      const packetGapMs =
        Number.isFinite(Number(nextMove.packetTs)) && previousVisualPacketTs > 0
          ? Number(nextMove.packetTs) - previousVisualPacketTs
          : null;
      const arrivalGapMs = Number.isFinite(Number(nextMove.arrivalGapMs)) ? Number(nextMove.arrivalGapMs) : null;
      const segmentDurationMs = resolveVisualDurationMs({
        packetGapMs,
        arrivalGapMs,
        movedM,
        speedKmh: speedNow,
      });
      const visualFrom = {
        lat: currentLat,
        lng: currentLng,
        timestamp: previousVisualPacketTs || now,
        heading: normalizeDeg(state.currentHeading ?? targetHeading),
      };
      const visualTo = {
        lat: targetLat,
        lng: targetLng,
        timestamp: Number.isFinite(Number(nextMove.packetTs)) ? Number(nextMove.packetTs) : now,
        heading: targetHeading,
        renderHeading: normalizeDeg(nextMove.renderHeading ?? targetHeading),
      };
      const turnPivot = resolveTurnPivot({
        from: { lat: currentLat, lng: currentLng },
        to: { lat: targetLat, lng: targetLng },
        fromHeading: normalizeDeg(state.currentHeading ?? targetHeading),
        toHeading: targetHeading,
        movedM,
      });

      state.segmentFromLat = currentLat;
      state.segmentFromLng = currentLng;
      state.segmentToLat = targetLat;
      state.segmentToLng = targetLng;
      state.headingFrom = normalizeDeg(state.currentHeading ?? targetHeading);
      state.headingTo = targetHeading;
      state.segmentStartAt = now;
      state.segmentDurationMs = segmentDurationMs;
      state.prev = visualFrom;
      state.next = visualTo;
      state.animStart = now;
      state.animDuration = segmentDurationMs;
      state.turnPivot = turnPivot;
      state.targetRenderHeading = normalizeDeg(nextMove.renderHeading ?? targetHeading);
      state.isAnimating = true;
      logLiveVisualDebug("segment-start", () => ({
        markerId: state.markerId,
        from: { lat: currentLat, lng: currentLng },
        to: { lat: targetLat, lng: targetLng },
        segmentDurationMs,
        queueDepth: Array.isArray(state.movementQueue) ? state.movementQueue.length : 0,
        packetTs: nextMove.packetTs ?? null,
      }));
      return true;
    }

    state.isAnimating = false;
    return false;
  }, [syncVisualStateToPoint]);

  const applyQueuedVehicleToVisualState = useCallback((markerId, vehicle, now = Date.now()) => {
    const existing = ensureLiveVisualState(markerId, vehicle, now);
    if (!existing) return false;

    const targetLat = Number(vehicle?.pos?.lat);
    const targetLng = Number(vehicle?.pos?.lng);
    if (!isValidGPS(targetLat, targetLng)) return false;

    const incomingTs = Number(vehicle?.packetTs);
    const resolvedIncomingTs = Number.isFinite(incomingTs) ? incomingTs : now;
    const prevPacketTs = Number(existing.lastPacketTs || 0);
    const incomingSource = vehicle?.dataSource || existing.lastDataSource || 'unknown';
    const previousTarget = {
      lat: Number(existing.targetLat),
      lng: Number(existing.targetLng),
    };
    const movedM = haversineMeters(previousTarget, { lat: targetLat, lng: targetLng });
    const stalePacket =
      Number.isFinite(resolvedIncomingTs) &&
      prevPacketTs > 0 &&
      resolvedIncomingTs + 30000 < prevPacketTs;
    const packetRenderHeading = normalizeDeg(
      vehicle?.rotation ??
      vehicle?.rawHeading ??
      existing.renderHeading ??
      existing.targetHeading ??
      0
    );

    const resolvedSpeed = resolveLiveSpeed(vehicle?.speed, existing.lastSpeed ?? existing.speed ?? 0);
    const resolvedAccStatus =
      vehicle?.accStatus !== undefined && vehicle?.accStatus !== null
        ? Boolean(vehicle.accStatus)
        : existing.accStatus ?? null;

    existing.speed = resolvedSpeed;
    existing.lastSpeed = resolvedSpeed;
    existing.accStatus = resolvedAccStatus;
    existing.status = vehicle?.status || existing.status || "no_data";
    existing.movementState = vehicle?.movementState || existing.movementState || null;
    existing.direction = vehicle?.direction || existing.direction || null;
    existing.turnType = vehicle?.turnType || existing.turnType || null;
    existing.roadSnapped = Boolean(vehicle?.roadSnapped);
    existing.roadSnapAgeMs = Number(vehicle?.roadSnapAgeMs ?? Infinity);
    existing.packetArrivedAt = now;
    existing.lastUpdateTime = now;
    const previousArrivalAt = Number(existing.lastPacketArrivalAt || 0);
    const rawArrivalGapMs =
      previousArrivalAt > 0 && now > previousArrivalAt
        ? now - previousArrivalAt
        : null;
    if (Number.isFinite(rawArrivalGapMs) && rawArrivalGapMs > 0) {
      const prevExpectedGapMs = Number(existing.expectedPacketGapMs || 0);
      const resolvedArrivalGapMs = clamp(
        rawArrivalGapMs,
        LIVE_VISUAL_EXPECTED_GAP_MIN_MS,
        LIVE_VISUAL_EXPECTED_GAP_MAX_MS
      );
      existing.expectedPacketGapMs =
        prevExpectedGapMs > 0
          ? clamp(
              prevExpectedGapMs * 0.45 + resolvedArrivalGapMs * 0.55,
              LIVE_VISUAL_EXPECTED_GAP_MIN_MS,
              LIVE_VISUAL_EXPECTED_GAP_MAX_MS
            )
          : resolvedArrivalGapMs;
    }
    existing.lastPacketArrivalAt = now;

    if (String(vehicle?.status || '').trim().toLowerCase() === 'expired') {
      const expiredHeading = normalizeDeg(vehicle?.rotation ?? vehicle?.rawHeading ?? existing.targetHeading ?? 0);
      syncVisualStateToPoint(existing, { lat: targetLat, lng: targetLng }, {
        heading: expiredHeading,
        renderHeading: packetRenderHeading,
        timestamp: resolvedIncomingTs,
      });
      existing.lastPacketTs = Number.isFinite(resolvedIncomingTs)
        ? Math.max(prevPacketTs, resolvedIncomingTs)
        : prevPacketTs || now;
      existing.lastDataSource = 'db';
      existing.speed = 0;
      existing.lastSpeed = 0;
      existing.accStatus = false;
      logLiveVisualDebug("packet-expired-snap", {
        markerId,
        packetTs: resolvedIncomingTs,
        position: { lat: targetLat, lng: targetLng },
      });
      return true;
    }

    // Never let DB snapshots move a marker backwards over a newer socket-driven state.
    if (incomingSource === 'db' && prevPacketTs > 0) {
      if (!Number.isFinite(incomingTs) || resolvedIncomingTs <= prevPacketTs) {
        return false;
      }
    }

    if (stalePacket) {
      return false;
    }

    const moveBearing = movedM >= 3 ? bearingBetween(previousTarget, { lat: targetLat, lng: targetLng }) : null;
    const targetHeading = normalizeDeg(
      moveBearing ?? vehicle?.rotation ?? vehicle?.rawHeading ?? existing.targetHeading ?? 0
    );

    existing.targetLat = targetLat;
    existing.targetLng = targetLng;
    existing.targetHeading = targetHeading;
    existing.targetRenderHeading = packetRenderHeading;
    existing.lastLat = targetLat;
    existing.lastLng = targetLng;
    existing.lastHeading = targetHeading;
    existing.lastPacketTs = Number.isFinite(resolvedIncomingTs)
      ? Math.max(prevPacketTs, resolvedIncomingTs)
      : prevPacketTs || now;
    existing.lastDataSource = incomingSource;

    const currentPosValid = isValidGPS(Number(existing.currentLat), Number(existing.currentLng));
    if (!currentPosValid) {
      syncVisualStateToPoint(existing, { lat: targetLat, lng: targetLng }, {
        heading: targetHeading,
        renderHeading: packetRenderHeading,
        timestamp: resolvedIncomingTs,
      });
      logLiveVisualDebug("visual-seed", {
        markerId,
        packetTs: resolvedIncomingTs,
        position: { lat: targetLat, lng: targetLng },
      });
      return true;
    }

    const speedNow = resolvedSpeed;
    const turnHint =
      Boolean(String(vehicle?.turnType || existing.turnType || "").trim()) ||
      Boolean(String(vehicle?.direction || existing.direction || "").trim()) ||
      String(vehicle?.movementState || existing.movementState || "").toLowerCase().includes("turn");
    const minMoveAcceptM = turnHint ? 0.03 : speedNow > 0 ? 0.08 : 0.2;
    if (movedM <= minMoveAcceptM) {
      if (existing.isAnimating) {
        queueVisualMove(existing, {
          lat: targetLat,
          lng: targetLng,
          packetTs: resolvedIncomingTs,
          arrivalGapMs:
            Number.isFinite(rawArrivalGapMs) && rawArrivalGapMs > 0
              ? rawArrivalGapMs
              : Number(existing.expectedPacketGapMs || LIVE_VISUAL_EXPECTED_GAP_MIN_MS),
          heading: targetHeading,
          renderHeading: packetRenderHeading,
          speed: resolvedSpeed,
          movementState: vehicle?.movementState || existing.movementState || null,
          direction: vehicle?.direction || existing.direction || null,
          turnType: vehicle?.turnType || existing.turnType || null,
        }, { replaceTail: true });
        logLiveVisualDebug("packet-coalesced-while-animating", {
          markerId,
          packetTs: resolvedIncomingTs,
          movedM,
          current: { lat: Number(existing.currentLat), lng: Number(existing.currentLng) },
          target: { lat: targetLat, lng: targetLng },
          queueDepth: existing.movementQueue.length,
        });
        return true;
      }

      syncVisualStateToPoint(existing, { lat: targetLat, lng: targetLng }, {
        heading: targetHeading,
        renderHeading: packetRenderHeading,
        timestamp: resolvedIncomingTs,
      });
      logLiveVisualDebug("packet-near-target-snap", {
        markerId,
        packetTs: resolvedIncomingTs,
        movedM,
        position: { lat: targetLat, lng: targetLng },
      });
      return true;
    }

    if (movedM > 2500) {
      syncVisualStateToPoint(existing, { lat: targetLat, lng: targetLng }, {
        heading: targetHeading,
        renderHeading: packetRenderHeading,
        timestamp: resolvedIncomingTs,
      });
      logLiveVisualDebug("packet-large-jump-reset", {
        markerId,
        packetTs: resolvedIncomingTs,
        movedM,
        position: { lat: targetLat, lng: targetLng },
      });
      return true;
    }

    const nextMove = {
      lat: targetLat,
      lng: targetLng,
      packetTs: resolvedIncomingTs,
      arrivalGapMs:
        Number.isFinite(rawArrivalGapMs) && rawArrivalGapMs > 0
          ? rawArrivalGapMs
          : Number(existing.expectedPacketGapMs || LIVE_VISUAL_EXPECTED_GAP_MIN_MS),
      heading: targetHeading,
      renderHeading: packetRenderHeading,
      speed: resolvedSpeed,
      movementState: vehicle?.movementState || existing.movementState || null,
      direction: vehicle?.direction || existing.direction || null,
      turnType: vehicle?.turnType || existing.turnType || null,
    };

    queueVisualMove(existing, nextMove);
    logLiveVisualDebug("packet-queued", {
      markerId,
      packetTs: resolvedIncomingTs,
      movedM,
      current: { lat: Number(existing.currentLat), lng: Number(existing.currentLng) },
      target: { lat: targetLat, lng: targetLng },
      queueDepth: existing.movementQueue.length,
      isAnimating: existing.isAnimating,
    });

    if (!existing.isAnimating) {
      return startNextQueuedVisualSegment(existing, now);
    }

    return true;
  }, [ensureLiveVisualState, queueVisualMove, startNextQueuedVisualSegment, syncVisualStateToPoint]);

  const animateLiveVisuals = useCallback(() => {
    liveVisualRafRef.current = null;
    const now = Date.now();
    const prevFrameAt = liveVisualFrameAtRef.current || now;
    const dtMs = clamp(now - prevFrameAt, 12, 80);
    liveVisualFrameAtRef.current = now;

    let shouldContinue = false;
    let anyChanged = false;

 liveVisualStateRef.current.forEach((state) => {

const isExpired = state.status === "expired";
const queuedMoves = Array.isArray(state.movementQueue) ? state.movementQueue.length : 0;
const packetArrivedAt = Number(state.packetArrivedAt || 0);
const sincePacketMs = packetArrivedAt > 0 ? now - packetArrivedAt : Number.POSITIVE_INFINITY;
const distanceToTargetM = haversineMeters(
  { lat: Number(state.currentLat), lng: Number(state.currentLng) },
  { lat: Number(state.targetLat), lng: Number(state.targetLng) }
);
const hasActiveDelta =
  Number.isFinite(distanceToTargetM) && distanceToTargetM >= LIVE_VISUAL_ACTIVE_DELTA_MIN_M;
const stateMovementKey = String(state.movementState || "").toLowerCase();
const stateStatusKey = String(state.status || "").toLowerCase();
const turnActive =
  Boolean(String(state.turnType || "").trim()) ||
  Boolean(String(state.direction || "").trim()) ||
  stateMovementKey.includes("turn");
const inferredMotion =
  state.isAnimating ||
  queuedMoves > 0 ||
  hasActiveDelta ||
  turnActive ||
  stateMovementKey === "moving" ||
  stateStatusKey === "running" ||
  stateStatusKey === "moving" ||
  Number(state.speed || 0) > 0;
const shouldAnimateVehicle =
  !isExpired &&
  (
    inferredMotion ||
    sincePacketMs <= LIVE_VISUAL_RECENT_PACKET_WINDOW_MS ||
    state.markerId === markerPopupDeviceIdRef.current ||
    selectedDevicesRef.current.includes(state.markerId)
  );

  if (!shouldAnimateVehicle) return;
      if (!state) return;
      const targetLat = Number(state.targetLat);
      const targetLng = Number(state.targetLng);
      if (!isValidGPS(targetLat, targetLng)) return;

      const targetHeading = normalizeDeg(state.targetHeading ?? state.currentHeading ?? 0);
      const currentLat = Number(state.currentLat);
      const currentLng = Number(state.currentLng);
      if (!isValidGPS(currentLat, currentLng)) {
        state.currentLat = targetLat;
        state.currentLng = targetLng;
        state.currentHeading = targetHeading;
        anyChanged = true;
        return;
      }

      const segmentDurationMs = Number((state.animDuration ?? state.segmentDurationMs) || 0);
      const segmentStartAt = Number((state.animStart ?? state.segmentStartAt) || 0);
      const hasSegment = segmentDurationMs > 0 && segmentStartAt > 0;

      if (hasSegment) {
        const elapsedMs = now - segmentStartAt;
        const prevPoint = state.prev || {
          lat: Number(state.segmentFromLat ?? currentLat),
          lng: Number(state.segmentFromLng ?? currentLng),
          heading: normalizeDeg(state.headingFrom ?? state.currentHeading ?? targetHeading),
        };
        const nextPoint = state.next || {
          lat: Number(state.segmentToLat ?? targetLat),
          lng: Number(state.segmentToLng ?? targetLng),
          heading: normalizeDeg(state.headingTo ?? targetHeading),
        };
        const fromLat = Number(prevPoint.lat);
        const fromLng = Number(prevPoint.lng);
        const toLat = Number(nextPoint.lat);
        const toLng = Number(nextPoint.lng);
        const fromHeading = normalizeDeg(prevPoint.heading ?? state.headingFrom ?? state.currentHeading ?? targetHeading);
        const toHeading = normalizeDeg(nextPoint.heading ?? state.headingTo ?? targetHeading);
        const turnPivot = state.turnPivot && isValidGPS(Number(state.turnPivot.lat), Number(state.turnPivot.lng))
          ? { lat: Number(state.turnPivot.lat), lng: Number(state.turnPivot.lng) }
          : null;
        const firstLegBearing = turnPivot
          ? bearingBetween({ lat: fromLat, lng: fromLng }, turnPivot)
          : bearingBetween({ lat: fromLat, lng: fromLng }, { lat: toLat, lng: toLng });
        const secondLegBearing = turnPivot
          ? bearingBetween(turnPivot, { lat: toLat, lng: toLng })
          : null;
        const lineBearing = bearingBetween({ lat: fromLat, lng: fromLng }, { lat: toLat, lng: toLng });
        const firstLegDistance = turnPivot
          ? haversineMeters({ lat: fromLat, lng: fromLng }, turnPivot)
          : 0;
        const secondLegDistance = turnPivot
          ? haversineMeters(turnPivot, { lat: toLat, lng: toLng })
          : 0;
        const totalTurnDistance = firstLegDistance + secondLegDistance;

        if (elapsedMs < segmentDurationMs) {
          const t = clamp(elapsedMs / segmentDurationMs, 0, 1);
          if (turnPivot && totalTurnDistance > 0) {
            const traveled = totalTurnDistance * t;
            if (traveled <= firstLegDistance) {
              const legT = clamp(traveled / Math.max(firstLegDistance, 1), 0, 1);
              const point = interpolatePoint({ lat: fromLat, lng: fromLng }, turnPivot, legT);
              state.currentLat = point.lat;
              state.currentLng = point.lng;
              state.currentHeading =
                firstLegBearing !== null
                  ? normalizeDeg(firstLegBearing)
                  : interpolateHeading(fromHeading, toHeading, t);
            } else {
              const legProgress = traveled - firstLegDistance;
              const legT = clamp(legProgress / Math.max(secondLegDistance, 1), 0, 1);
              const point = interpolatePoint(turnPivot, { lat: toLat, lng: toLng }, legT);
              state.currentLat = point.lat;
              state.currentLng = point.lng;
              state.currentHeading =
                secondLegBearing !== null
                  ? normalizeDeg(secondLegBearing)
                  : interpolateHeading(fromHeading, toHeading, t);
            }
          } else {
            const point = interpolatePoint({ lat: fromLat, lng: fromLng }, { lat: toLat, lng: toLng }, t);
            state.currentLat = point.lat;
            state.currentLng = point.lng;
            state.currentHeading =
              lineBearing !== null
                ? normalizeDeg(lineBearing)
                : interpolateHeading(fromHeading, toHeading, t);
          }
          anyChanged = true;
          shouldContinue = true;
          logLiveVisualDebug("frame", () => ({
            markerId: state.markerId,
            progress: Number(t.toFixed(3)),
            current: {
              lat: Number(state.currentLat).toFixed(6),
              lng: Number(state.currentLng).toFixed(6),
            },
            target: {
              lat: Number(toLat).toFixed(6),
              lng: Number(toLng).toFixed(6),
            },
            queueDepth: queuedMoves,
            isAnimating: true,
          }));
          return;
        }

        state.currentLat = toLat;
        state.currentLng = toLng;
        state.currentHeading = lineBearing !== null ? normalizeDeg(lineBearing) : toHeading;
        state.segmentDurationMs = 0;
        state.segmentStartAt = 0;
        state.animDuration = 0;
        state.animStart = 0;
        state.turnPivot = null;
        state.visualPacketTs = Number(nextPoint.timestamp || now);
        state.isAnimating = false;
        state.renderHeading = normalizeDeg(nextPoint.renderHeading ?? state.currentHeading);
        state.targetRenderHeading = state.renderHeading;
        state.prev = { lat: toLat, lng: toLng, timestamp: Number(nextPoint.timestamp || now), heading: state.currentHeading };
        state.next = { lat: toLat, lng: toLng, timestamp: Number(nextPoint.timestamp || now), heading: state.currentHeading };
        anyChanged = true;
        logLiveVisualDebug("segment-complete", () => ({
          markerId: state.markerId,
          current: { lat: toLat, lng: toLng },
          queueDepth: Array.isArray(state.movementQueue) ? state.movementQueue.length : 0,
          packetTs: Number(nextPoint.timestamp || now),
        }));

        if (startNextQueuedVisualSegment(state, now)) {
          shouldContinue = true;
        }
      }

      const speed = Number(state.speed || 0);
      const accStatus = state.accStatus;
      const isMoving =
        accStatus !== false &&
        (speed > 0 ||
          turnActive ||
          String(state.movementState || "").toLowerCase() === "moving" ||
          String(state.status || "").toLowerCase() === "running");
      const expectedPacketGapMs = clamp(
        Number(state.expectedPacketGapMs || LIVE_VISUAL_EXPECTED_GAP_MIN_MS),
        LIVE_VISUAL_EXPECTED_GAP_MIN_MS,
        LIVE_VISUAL_EXPECTED_GAP_MAX_MS
      );
      const extrapolationWindowMs = clamp(
        expectedPacketGapMs * LIVE_VISUAL_EXPECTED_GAP_BUFFER_RATIO,
        LIVE_VISUAL_EXTRAPOLATION_MAX_MS,
        LIVE_VISUAL_EXPECTED_GAP_MAX_MS
      );

      // Short dead-reckoning so marker does not visually freeze between sparse packets.
      // Keep strict road lock only while snapped point is very fresh.
      const snapLockActive =
        state.roadSnapped &&
        Number.isFinite(Number(state.roadSnapAgeMs)) &&
        Number(state.roadSnapAgeMs) <= LIVE_VISUAL_SNAP_LOCK_MAX_MS;
      if (
        !snapLockActive &&
        isMoving &&
        sincePacketMs >= LIVE_VISUAL_EXTRAPOLATION_START_MS &&
        sincePacketMs <= extrapolationWindowMs
      ) {
        const heading = normalizeDeg(state.currentHeading ?? targetHeading);
        const stepMeters = (speed / 3.6) * (dtMs / 1000);
        const projected = projectPoint(
          { lat: Number(state.currentLat), lng: Number(state.currentLng) },
          heading,
          stepMeters
        );
        if (projected && isValidGPS(projected.lat, projected.lng)) {
          state.currentLat = Number(projected.lat);
          state.currentLng = Number(projected.lng);
          anyChanged = true;
          shouldContinue = true;
          logLiveVisualDebug("dead-reckon", () => ({
            markerId: state.markerId,
            current: {
              lat: Number(state.currentLat).toFixed(6),
              lng: Number(state.currentLng).toFixed(6),
            },
            speed,
            sincePacketMs,
          }));
        }
      }

      const headingNow = normalizeDeg(state.currentHeading ?? targetHeading);
      if (headingNow !== targetHeading) {
        state.currentHeading = targetHeading;
        anyChanged = true;
      }

      if (!state.isAnimating) {
        const stableRenderHeading = normalizeDeg(
          state.targetRenderHeading ??
          state.renderHeading ??
          state.currentHeading ??
          targetHeading
        );
        const currentRenderHeading = normalizeDeg(
          state.renderHeading ?? stableRenderHeading
        );
        if (stableRenderHeading !== currentRenderHeading) {
          state.renderHeading = stableRenderHeading;
          anyChanged = true;
        }
      }
    });

    if (anyChanged) {
      scheduleImperativeMarkerSync();
      syncFocusedVehicleLivePosition();
    }
    if (shouldContinue && liveVisualStateRef.current.size > 0) {
      liveVisualRafRef.current = requestAnimationFrame(animateLiveVisuals);
    }
  }, [scheduleImperativeMarkerSync, startNextQueuedVisualSegment, syncFocusedVehicleLivePosition]);

  useEffect(() => {
    const liveIds = new Set();

    processedVehicles.forEach((vehicle) => {
      const markerId = String(vehicle?.device?._id || vehicle?.device?.imei || "");
      if (!markerId) return;
      liveIds.add(markerId);

      const targetLat = Number(vehicle?.pos?.lat);
      const targetLng = Number(vehicle?.pos?.lng);
      if (!isValidGPS(targetLat, targetLng)) return;

      const existing = liveVisualStateRef.current.get(markerId);
      const now = Date.now();

      if (!existing) {
        const seeded = ensureLiveVisualState(markerId, vehicle, now);
        if (seeded) seeded.markerId = markerId;
        return;
      }

      existing.markerId = markerId;
      if (vehicle?.dataSource === 'socket') {
        const incomingSocketTs = Number(vehicle?.packetTs || 0);
        const lastAppliedTs = Number(existing?.lastPacketTs || 0);
        if (Number.isFinite(incomingSocketTs) && incomingSocketTs > lastAppliedTs) {
          logLiveVisualDebug("processed-vehicles-apply-socket", {
            markerId,
            incomingSocketTs,
            lastAppliedTs,
            position: { lat: targetLat, lng: targetLng },
          });
          applyQueuedVehicleToVisualState(markerId, vehicle, now);
          return;
        }

        existing.speed = Number(vehicle?.speed || existing.speed || 0);
        existing.status = vehicle?.status || existing.status || "no_data";
        existing.movementState = vehicle?.movementState || existing.movementState || null;
        existing.direction = vehicle?.direction || existing.direction || null;
        existing.turnType = vehicle?.turnType || existing.turnType || null;
        existing.roadSnapped = Boolean(vehicle?.roadSnapped);
        existing.roadSnapAgeMs = Number(vehicle?.roadSnapAgeMs ?? Infinity);
        return;
      }

      const incomingDbTs = Number(vehicle?.packetTs || 0);
      const lastAppliedTs = Number(existing?.lastPacketTs || 0);
      if (lastAppliedTs > 0 && (!Number.isFinite(incomingDbTs) || incomingDbTs <= lastAppliedTs)) {
        existing.speed = Number(vehicle?.speed || existing.speed || 0);
        existing.status = vehicle?.status || existing.status || "no_data";
        existing.movementState = vehicle?.movementState || existing.movementState || null;
        existing.direction = vehicle?.direction || existing.direction || null;
        existing.turnType = vehicle?.turnType || existing.turnType || null;
        existing.roadSnapped = Boolean(vehicle?.roadSnapped);
        existing.roadSnapAgeMs = Number(vehicle?.roadSnapAgeMs ?? Infinity);
        return;
      }

      logLiveVisualDebug("processed-vehicles-apply-db", {
        markerId,
        incomingDbTs,
        lastAppliedTs,
        position: { lat: targetLat, lng: targetLng },
      });
      applyQueuedVehicleToVisualState(markerId, vehicle, now);
    });

    for (const markerId of Array.from(liveVisualStateRef.current.keys())) {
      if (!liveIds.has(markerId)) {
        liveVisualStateRef.current.delete(markerId);
      }
    }

    processedVehiclesRef.current = processedVehicles;
    scheduleImperativeMarkerSync();
    syncFocusedVehicleLivePosition(true);
    if (!liveVisualRafRef.current && liveVisualStateRef.current.size > 0) {
      liveVisualFrameAtRef.current = Date.now();
      liveVisualRafRef.current = requestAnimationFrame(animateLiveVisuals);
    }
  }, [animateLiveVisuals, processedVehicles, scheduleImperativeMarkerSync, syncFocusedVehicleLivePosition]);

  useEffect(() => {
    return () => {
      if (followResumeTimerRef.current) {
        clearTimeout(followResumeTimerRef.current);
      }
      if (followPanRafRef.current) {
        cancelAnimationFrame(followPanRafRef.current);
      }
      if (liveVisualRafRef.current) {
        cancelAnimationFrame(liveVisualRafRef.current);
      }
      followResumeTimerRef.current = null;
      followPanRafRef.current = null;
      followPauseUntilRef.current = 0;
      liveVisualRafRef.current = null;
      liveVisualStateRef.current.clear();
      if (markerSyncRafRef.current) {
        cancelAnimationFrame(markerSyncRafRef.current);
      }
      markerSyncRafRef.current = null;
      clearImperativeMarkers();
    };
  }, [clearImperativeMarkers]);

  useEffect(() => {
    const buildVehicleFromPacket = (packet) => {
      const packetDeviceId = String(packet?.deviceId || packet?._id || "").trim();
      const packetImei = String(packet?.imei || "").trim();
      const device =
        devicesLookupRef.current.get(packetDeviceId) ||
        devicesLookupRef.current.get(packetImei) ||
        null;
      const markerId = String(device?._id || packetDeviceId || packetImei || "").trim();
      if (!markerId) return null;

      const lat = pickFinite(packet?.latitude, packet?.lat);
      const lng = pickFinite(packet?.longitude, packet?.lng);
      if (!isValidGPS(lat, lng)) return null;

      const packetTs = toTimestampMs(
        packet?.packetReceivedAt ??
          packet?.lastSocketUpdate ??
          packet?.serverReceivedAt ??
          packet?.receivedAt ??
          packet?.packetTime ??
          packet?.timestamp ??
          packet?.serverTime ??
          packet?.deviceTimestamp
      ) || Date.now();

      const prevTracking = trackingStateRef.current.get(markerId) || null;
      const canonicalStatus = device ? resolveUiDeviceStatus(device, [packet]).status : null;
      const resolvedStatus = device
        ? canonicalStatus
        : packet?.vehicleStatus ?? packet?.vehicle_status ?? "no_data";
      if (resolvedStatus === 'expired') {
        return null;
      }
      const packetAcc =
        packet?.accStatus ?? packet?.acc ?? packet?.ignition ?? prevTracking?.accStatus ?? null;
      const packetSpeedValue = Number(packet?.speed);
      const shouldForceZeroSpeed =
        (
          isStationaryStatus(canonicalStatus ?? resolvedStatus) ||
          packetAcc === false
        ) &&
        (!Number.isFinite(packetSpeedValue) || packetSpeedValue <= 0);
      const resolvedSpeed = shouldForceZeroSpeed
        ? 0
        : resolveLiveSpeed(packet?.speed, prevTracking?.speed ?? 0);
      const tracking = resolveTrackingState({
        prev: prevTracking,
        speed: resolvedSpeed,
        accStatus: packetAcc,
        timestampMs: packetTs,
        latitude: lat,
        longitude: lng,
        heading: pickFinite(packet?.heading, packet?.course, packet?.bearing),
        vehicleStatus:
          canonicalStatus ??
          packet?.vehicleStatus ??
          packet?.vehicle_status ??
          packet?.status ??
          resolvedStatus,
        movementState: packet?.movementState ?? packet?.movement_state ?? null,
        direction: packet?.direction ?? null,
        turnType: packet?.turnType ?? packet?.turn_type ?? null,
      });
      trackingStateRef.current.set(markerId, tracking);

      return {
        markerId,
        device: device || { _id: markerId, imei: packetImei || undefined, name: packetImei || markerId },
        pos: { lat: Number(lat), lng: Number(lng) },
        rawPos: { lat: Number(lat), lng: Number(lng) },
        rotation: normalizeDeg(tracking.heading),
        rawHeading: normalizeDeg(tracking.heading),
        movementState: tracking.movementState,
        direction: tracking.direction,
        turnType: tracking.turnType,
        status: resolvedStatus,
        isSelected: selectedDevices.includes(markerId),
        isWired: device?.isWiredGPS || false,
        speed: resolvedSpeed,
        accStatus: tracking.accStatus ?? false,
        dataAge: null,
        dataSource: 'socket',
        packetTs,
        roadSnapped: false,
        roadSnapAgeMs: Infinity,
      };
    };

    const enqueueLivePacket = (packet) => {
      const vehicle = buildVehicleFromPacket(packet);
      if (!vehicle) return;

      const markerId = String(vehicle.markerId);
      const state = ensureLiveVisualState(markerId, vehicle, Date.now());
      if (state) state.markerId = markerId;
      const lastAppliedTs = Number(state?.lastPacketTs || 0);
      const packetTs = Number(vehicle.packetTs || 0);

      if (packetTs > 0 && lastAppliedTs > 0 && packetTs < lastAppliedTs) {
        logLiveVisualDebug("socket-packet-dropped-stale", {
          markerId,
          packetTs,
          lastAppliedTs,
        });
        return;
      }
      logLiveVisualDebug("socket-packet-received", {
        markerId,
        packetTs,
        lastAppliedTs,
        position: {
          lat: Number(vehicle?.pos?.lat).toFixed(6),
          lng: Number(vehicle?.pos?.lng).toFixed(6),
        },
      });
      applyQueuedVehicleToVisualState(markerId, vehicle, Date.now());

      scheduleImperativeMarkerSync();
      syncFocusedVehicleLivePosition();
      if (!liveVisualRafRef.current && liveVisualStateRef.current.size > 0) {
        liveVisualFrameAtRef.current = Date.now();
        liveVisualRafRef.current = requestAnimationFrame(animateLiveVisuals);
      }
    };

    const offPacket = on("gps_packet", enqueueLivePacket);
    const offLocation = on("location_update", enqueueLivePacket);
    const offGps = on("gps_update", enqueueLivePacket);
    const offVehicle = on("vehicle_update", enqueueLivePacket);
    const offStatus = on("status_update", enqueueLivePacket);

    return () => {
      offPacket?.();
      offLocation?.();
      offGps?.();
      offVehicle?.();
      offStatus?.();
    };
  }, [
    applyQueuedVehicleToVisualState,
    animateLiveVisuals,
    ensureLiveVisualState,
    on,
    selectedDevices,
    scheduleImperativeMarkerSync,
    syncFocusedVehicleLivePosition,
  ]);

  const displayedVehicles = useMemo(() => {
    if (isReplayMode) {
      return processedVehicles.filter((vehicle) => {
        const vehicleId = String(vehicle?.device?._id || "").trim();
        const vehicleImei = String(vehicle?.device?.imei || "").trim();
        if (vehicleId && hiddenLiveMarkers?.has?.(vehicleId)) return false;
        if (vehicleImei && hiddenLiveMarkers?.has?.(vehicleImei)) return false;
        return true;
      });
    }

    const playbackDeviceId = String(playbackData?.deviceId || "").trim();
    const playbackImei = String(playbackData?.imei || "").trim();
    if (!playbackDeviceId && !playbackImei) return processedVehicles;

    return processedVehicles.filter((vehicle) => {
      const vehicleId = String(vehicle?.device?._id || "").trim();
      const vehicleImei = String(vehicle?.device?.imei || "").trim();
      if (playbackDeviceId && vehicleId === playbackDeviceId) return false;
      if (playbackImei && vehicleImei === playbackImei) return false;
      return true;
    });
  }, [
    hiddenLiveMarkers,
    isReplayMode,
    playbackData?.deviceId,
    playbackData?.imei,
    processedVehicles,
  ]);

  const clusterPresentation = useMemo(() => {
    if (mapMode !== "cluster") {
      return {
        isClusterMode: false,
        clusters: [],
        singles: displayedVehicles,
      };
    }

    if (Array.isArray(serverClusters) && serverClusters.length > 0) {
      const selectedIds = new Set(
        (Array.isArray(selectedDevices) ? selectedDevices : [])
          .map((id) => String(id || "").trim())
          .filter(Boolean)
      );
      return {
        isClusterMode: true,
        clusters: serverClusters,
        singles: displayedVehicles.filter((vehicle) =>
          selectedIds.has(String(vehicle?.device?._id || vehicle?.device?.imei || ""))
        ),
      };
    }

    const clusterInput = displayedVehicles.map((vehicle) => ({
      id: String(vehicle?.device?._id || vehicle?.device?.imei || ""),
      lat: Number(vehicle?.pos?.lat),
      lng: Number(vehicle?.pos?.lng),
      status: vehicle?.status || "inactive",
      vehicle,
    }));

    const selectedIds = new Set(
      (Array.isArray(selectedDevices) ? selectedDevices : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    );

    const presentation = buildFleetClusters(clusterInput, {
      zoom: Number(mapZoom || 0),
      bounds: viewportSnapshot?.bounds || null,
      selectedIds,
    });

    return {
      isClusterMode: Boolean(presentation?.isClusterMode),
      clusters: Array.isArray(presentation?.clusters) ? presentation.clusters : [],
      singles: Array.isArray(presentation?.singles)
        ? presentation.singles
            .map((point) => point?.vehicle)
            .filter(Boolean)
        : displayedVehicles,
    };
  }, [
    displayedVehicles,
    mapMode,
    mapZoom,
    selectedDevices,
    serverClusters,
    viewportSnapshot,
  ]);

  const hasServerClusterBubbles =
    clusterPresentation.isClusterMode && Array.isArray(serverClusters) && serverClusters.length > 0;
  const hasClientClusterBubbles =
    clusterPresentation.isClusterMode && !hasServerClusterBubbles && clusterPresentation.clusters.length > 0;

const individuallyRenderedVehicles = useMemo(() => {
  return clusterPresentation.isClusterMode ? clusterPresentation.singles : displayedVehicles;
}, [clusterPresentation, displayedVehicles]);

  // Re-run the one-time automatic fit when the sidebar loads another page.
  // Only ids are included, so incoming live-position packets do not keep
  // re-fitting a map that the user is already viewing.
  const renderedVehicleIdsKey = useMemo(
    () =>
      individuallyRenderedVehicles
        .map((vehicle) => String(vehicle?.device?._id || vehicle?.device?.imei || ""))
        .filter(Boolean)
        .sort()
        .join("|"),
    [individuallyRenderedVehicles]
  );

  useEffect(() => {
    processedVehiclesRef.current = processedVehicles;
  }, [processedVehicles]);

  useEffect(() => {
    displayedVehiclesRef.current = displayedVehicles;
    renderedVehiclesRef.current = individuallyRenderedVehicles;
    const nextLookup = new Map();
    displayedVehicles.forEach((vehicle) => {
      const markerId = String(vehicle?.device?._id || vehicle?.device?.imei || "");
      if (markerId) nextLookup.set(markerId, vehicle);
    });
    displayedVehiclesLookupRef.current = nextLookup;
    scheduleImperativeMarkerSync();
    syncFocusedVehicleLivePosition(true);
  }, [displayedVehicles, individuallyRenderedVehicles, scheduleImperativeMarkerSync, syncFocusedVehicleLivePosition]);

  useEffect(() => {
    if (!userInteractedRef.current && selectedDevices.length === 0) {
      didInitialViewportFitRef.current = false;
    }
  }, [renderedVehicleIdsKey, selectedDevices.length]);

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;
    if (didInitialViewportFitRef.current) return;
    if (userInteractedRef.current) return;
    if (playbackData || isReplayMode) return;
    if (markerPopupDeviceId) return;
    if (selectedNotification) return;
    if (selectedDevices.length === 1) return;

    const isInitialLoadedBatch =
      individuallyRenderedVehicles.length > 0 && individuallyRenderedVehicles.length <= 250;
    // The initial live-tracking state is an India overview. Do not let a first
    // page of vehicles (often all parked around one depot) change it into a
    // single-city/street view. A row click or the second checkbox still goes
    // through the explicit single-vehicle focus flow and zooms in normally.
    if (isInitialLoadedBatch) {
      mapRef.current.setCenter(defaultCenter);
      mapRef.current.setZoom(DEFAULT_INDIA_ZOOM);
      didInitialViewportFitRef.current = true;
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    let validCount = 0;

    const fitVehicles = clusterPresentation.isClusterMode
      ? clusterPresentation.singles
      : displayedVehicles;
    const fitClusters = clusterPresentation.isClusterMode ? clusterPresentation.clusters : [];

    fitVehicles.forEach((vehicle) => {
      if (isValidGPS(Number(vehicle?.pos?.lat), Number(vehicle?.pos?.lng))) {
        bounds.extend(new window.google.maps.LatLng(Number(vehicle.pos.lat), Number(vehicle.pos.lng)));
        validCount += 1;
      }
    });

    fitClusters.forEach((cluster) => {
      const lat = Number(cluster?.latitude ?? cluster?.lat);
      const lng = Number(cluster?.longitude ?? cluster?.lng);
      if (isValidMapCoordinate(lat, lng)) {
        bounds.extend(new window.google.maps.LatLng(lat, lng));
        validCount += 1;
      }
    });

    if (!validCount || bounds.isEmpty?.()) return;

    mapRef.current.fitBounds(bounds, {
      top: 80,
      right: 80,
      bottom: 80,
      left: 80,
    });

    didInitialViewportFitRef.current = true;
  }, [
    clusterPresentation,
    displayedVehicles,
    individuallyRenderedVehicles,
    isReplayMode,
    markerPopupDeviceId,
    playbackData,
    selectedDevices,
    selectedNotification,
  ]);

  useEffect(() => {
    if (playbackData || isReplayMode) return;
    if (!mapRef.current || selectedDevices.length !== 1) return;

    const markerId = String(selectedDevices[0] || "");
    if (!markerId) return;

    const selectedVehicle =
      displayedVehiclesLookupRef.current.get(markerId) ||
      processedVehiclesRef.current.find(
        (vehicle) => String(vehicle?.device?._id || vehicle?.device?.imei || "") === markerId
      );
    const position = getCurrentMarkerPosition(markerId, selectedVehicle?.pos);
    if (!position) return;

    const shouldOffsetForPopup = String(markerPopupDeviceId || "") === markerId;
    const viewportState = getMarkerViewportState(mapRef.current, position, {
      popupOpen: shouldOffsetForPopup,
    });
    if (viewportState && !viewportState.outsideSafeZone) {
      return;
    }
    const panTarget = getMapCenterForMarkerAnchor(mapRef.current, position, {
      anchorYRatio: shouldOffsetForPopup ? POPUP_FOCUS_ANCHOR_Y_RATIO : 0.5,
    }) || position;

    mapRef.current.panTo(panTarget);
    const currentZoom = Number(mapRef.current.getZoom?.() || 0);
    if (currentZoom < 16) {
      mapRef.current.setZoom(16);
    }
  }, [getCurrentMarkerPosition, isReplayMode, markerPopupDeviceId, playbackData, selectedDevices]);

  useEffect(() => {
    if (!playbackData) return;
    setMarkerPopupDeviceId(null);
    setMarkerPopupAddress("");
  }, [playbackData]);

  const activeTripRoutes = useMemo(() => {
    const rows = Array.isArray(tripsData) ? tripsData : [];
    const selectedSet = new Set(selectedDevices.map(String));

    let filtered = rows
      .filter((trip) => {
        if (forceTripId && String(trip?._id) !== String(forceTripId)) return false;
        if (forceTripVehicleId) {
          const vid = String(trip?.vehicle?._id || trip?.vehicle || '');
          if (vid !== String(forceTripVehicleId)) return false;
        }

        const st = String(trip?.tripStatus || trip?.status || "").toLowerCase();
        const isActive = forceTripId || forceTripVehicleId
          ? ["planned", "scheduled", "started", "in_progress", "active"].includes(st)
          : ["started", "in_progress", "active"].includes(st);
        if (!isActive) return false;
        if (!selectedSet.size) return true;
        return selectedSet.has(String(trip?.vehicle?._id || trip?.vehicle));
      });

    if ((forceTripId || forceTripVehicleId) && filtered.length > 1) {
      filtered = filtered.sort((a, b) => {
        const aTime = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
        const bTime = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
        return bTime - aTime;
      }).slice(0, 1);
    }

    return filtered
      .map((trip) => {
        const sLat = Number(trip?.startPoint?.lat);
        const sLng = Number(trip?.startPoint?.lng);
        const eLat = Number(trip?.endPoint?.lat);
        const eLng = Number(trip?.endPoint?.lng);
        if (![sLat, sLng, eLat, eLng].every(Number.isFinite)) return null;

        return {
          id: String(trip?._id || Math.random()),
          name: trip?.routeName || "Trip",
          path: [
            { lat: sLat, lng: sLng },
            { lat: eLat, lng: eLng },
          ],
          start: { lat: sLat, lng: sLng },
          end: { lat: eLat, lng: eLng },
        };
      })
      .filter(Boolean);
  }, [tripsData, selectedDevices, forceTripId, forceTripVehicleId]);

  const forcedRouteOverlay = useMemo(() => {
    if (!forceRoute) return [];
    const sLat = Number(forceRoute?.source?.lat);
    const sLng = Number(forceRoute?.source?.lng);
    const eLat = Number(forceRoute?.destination?.lat);
    const eLng = Number(forceRoute?.destination?.lng);
    if (![sLat, sLng, eLat, eLng].every(Number.isFinite)) return [];

    return [{
      id: `manual-route-${forceRoute?._id || forceRoute?.routeName || "route"}`,
      name: forceRoute?.routeName || "Route",
      path: [{ lat: sLat, lng: sLng }, { lat: eLat, lng: eLng }],
      start: { lat: sLat, lng: sLng },
      end: { lat: eLat, lng: eLng },
      isForcedRoute: true,
    }];
  }, [forceRoute]);

  const forcedRoutesOverlay = useMemo(() => {
    const rows = Array.isArray(forceRoutes) ? forceRoutes : [];
    if (!rows.length) return [];

    return rows
      .map((route) => {
        const sLat = Number(route?.start?.lat ?? route?.source?.lat);
        const sLng = Number(route?.start?.lng ?? route?.source?.lng);
        const eLat = Number(route?.end?.lat ?? route?.destination?.lat);
        const eLng = Number(route?.end?.lng ?? route?.destination?.lng);
        if (![sLat, sLng, eLat, eLng].every(Number.isFinite)) return null;

        return {
          id: String(route?.id || route?._id || route?.routeName || Math.random()),
          name: route?.name || route?.routeName || "Route",
          path: [{ lat: sLat, lng: sLng }, { lat: eLat, lng: eLng }],
          start: { lat: sLat, lng: sLng },
          end: { lat: eLat, lng: eLng },
          isForcedRoute: true,
        };
      })
      .filter(Boolean);
  }, [forceRoutes]);

  const overlayRoutes = useMemo(() => {
    if (forcedRoutesOverlay.length) return forcedRoutesOverlay;
    if (forcedRouteOverlay.length) return forcedRouteOverlay;
    return activeTripRoutes;
  }, [forcedRoutesOverlay, forcedRouteOverlay, activeTripRoutes]);

  const handleViewAll = useCallback(() => {
    if (!map || displayedVehicles.length === 0) return;

    try {
      const bounds = new window.google.maps.LatLngBounds();
      displayedVehicles.forEach((v) => {
        if (isValidGPS(v.pos.lat, v.pos.lng)) {
          bounds.extend(new window.google.maps.LatLng(v.pos.lat, v.pos.lng));
        }
      });

      map.fitBounds(bounds, {
        top: 80,
        right: 80,
        bottom: 80,
        left: 80
      });

      if (setFollowMode) setFollowMode(false);
      setMarkerPopupDeviceId(null);
      setSelectedDevices([]);
      userInteractedRef.current = false;
    } catch (err) {
      console.error('View All error:', err);
    }
  }, [map, displayedVehicles, setFollowMode, setSelectedDevices]);

  const followTargetVehicleLivePos = focusedVehicleLivePos;

  const selectedVehicleRecord = useMemo(() => {
    const selectedId = String(markerPopupDeviceId || "");
    if (!selectedId) return null;
    const visibleVehicle =
      displayedVehicles.find((vehicle) => String(vehicle?.device?._id || "") === selectedId) ||
      processedVehicles.find((vehicle) => String(vehicle?.device?._id || "") === selectedId);
    if (!visibleVehicle) return null;

    const device = visibleVehicle.device || devicesLookupRef.current.get(selectedId) || null;
    const isExpiredVehicle = resolveUiDeviceStatus(device || {}).status === 'expired';
    const socketData =
      isExpiredVehicle
        ? {}
        :
      socketUpdatesRef?.current?.[selectedId] ||
      socketUpdatesRef?.current?.[device?.imei] ||
      {};

    const liveAddress =
      normalizeAddressText(resolvedAddressHint) ||
      normalizeAddressText(socketData?.address?.full) ||
      normalizeAddressText(socketData?.address?.short) ||
      normalizeAddressText(socketData?.address) ||
      normalizeAddressText(device?.address?.full) ||
      normalizeAddressText(device?.address?.short) ||
      normalizeAddressText(device?.address) ||
      normalizeAddressText(device?.currentAddress) ||
      normalizeAddressText(device?.locationAddress) ||
      normalizeAddressText(device?.lastKnownAddress) ||
      normalizeAddressText(markerPopupAddress) ||
      "";
    const liveTelemetrySources = [
      socketData,
      device?.liveData,
      device?.currentLocation,
      device,
      visibleVehicle,
    ];

    const odometerValue =
      pickFinite(
        socketData?.odometer,
        socketData?.totalDistance,
        socketData?.totalOdo,
        socketData?.totalOdoRawKm,
        socketData?.mileage,
        device?.totalOdo,
        device?.totalOdoRawKm,
        device?.odometer,
        device?.liveData?.odometer,
        device?.liveData?.totalDistance,
        device?.currentLocation?.odometer,
        device?.currentLocation?.totalDistance
      ) ?? 0;
    const engineHoursValue =
      pickFinite(socketData?.engineHours, socketData?.engineHour, device?.engineHours) ?? 0;
    const voltageValue = pickTelemetryFromSources(liveTelemetrySources, [
      "voltage",
      "externalVoltage",
      "batteryVoltage",
    ]);
    const batteryValue = pickTelemetryFromSources(liveTelemetrySources, [
      "battery",
      "batteryPercent",
      "batteryLevel",
    ]);
    const temperatureValue = pickTelemetryFromSources(liveTelemetrySources, [
      "temperature",
      "temp",
    ]);
    const packetTime =
      visibleVehicle?.packetTs ??
      socketData?.packetReceivedAt ??
      socketData?.lastSocketUpdate ??
      socketData?.packetTime ??
      socketData?.timestamp ??
      device?.lastDataReceivedAt ??
      device?.updatedAt ??
      null;

    return {
      ...visibleVehicle,
      status:
        visibleVehicle?.status ??
        (device
          ? resolveUiDeviceStatus(device, [socketData]).status
          : visibleVehicle.status),
      speed: isExpiredVehicle ? 0 : Number(visibleVehicle.speed || 0),
      device,
      address: typeof liveAddress === "string" ? liveAddress : "",
      odometerKm: Number(odometerValue || 0),
      engineHours: Number(engineHoursValue || 0),
      voltage: voltageValue,
      battery: batteryValue,
      temperature: temperatureValue,
      packetTime,
    };
  }, [
    displayedVehicles,
    markerPopupAddress,
    markerPopupDeviceId,
    resolvedAddressHint,
    processedVehicles,
    socketUpdatesRef,
  ]);

  const selectedVehiclePopupPosition = useMemo(() => {
    if (!markerPopupDeviceId || !focusedVehicleLivePos) return null;
    return {
      lat: Number(focusedVehicleLivePos.lat),
      lng: Number(focusedVehicleLivePos.lng),
    };
  }, [focusedVehicleLivePos, markerPopupDeviceId]);

  useEffect(() => {
    if (!markerPopupDeviceId) return;
    const exists = displayedVehicles.some(
      (vehicle) => String(vehicle?.device?._id || "") === String(markerPopupDeviceId)
    );
    if (!exists) {
      setMarkerPopupDeviceId(null);
    }
  }, [displayedVehicles, markerPopupDeviceId]);

  useEffect(() => {
    if (!selectedVehicleRecord?.device?._id || !selectedVehiclePopupPosition) {
      setMarkerPopupAddress("");
      return;
    }

    const lat = Number(selectedVehiclePopupPosition.lat);
    const lng = Number(selectedVehiclePopupPosition.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const existingAddress = normalizeAddressText(selectedVehicleRecord.address);
    if (existingAddress) {
      setMarkerPopupAddress(existingAddress);
      return;
    }

    const deviceKey = String(selectedVehicleRecord.device._id);
    const now = Date.now();
    const prev = markerPopupGeocodeRef.current;

    const distanceMeters = (a, b) => {
      if (!a || !b) return Infinity;
      const R = 6371000;
      const dLat = ((b.lat - a.lat) * Math.PI) / 180;
      const dLng = ((b.lng - a.lng) * Math.PI) / 180;
      const x =
        dLat * dLat +
        Math.cos((a.lat * Math.PI) / 180) *
          Math.cos((b.lat * Math.PI) / 180) *
          dLng *
          dLng;
      return Math.sqrt(x) * R;
    };

    const moved =
      prev.key !== deviceKey
        ? Infinity
        : distanceMeters({ lat: prev.lat, lng: prev.lng }, { lat, lng });
    const elapsed = prev.key !== deviceKey ? Infinity : now - prev.at;
    if (moved < 20 && elapsed < 45000 && markerPopupAddress) return;

    let cancelled = false;

    const fetchAddress = async () => {
      try {
        const token = localStorage.getItem("token");
        const baseUrl = process.env.REACT_APP_BACKEND_URL || "https://qylmox.in/api";
        const response = await fetch(
          `${baseUrl}/devices/${selectedVehicleRecord.device._id}/address?lat=${lat}&lng=${lng}`,
          {
            headers: {
              "x-auth-token": token,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) throw new Error("Address fetch failed");
        const data = await response.json();
        const normalized =
          normalizeAddressText(data?.address) ||
          normalizeAddressText(data?.address?.full) ||
          normalizeAddressText(data?.address?.short);

        if (!cancelled && normalized) {
          setMarkerPopupAddress(normalized);
          markerPopupGeocodeRef.current = {
            key: deviceKey,
            lat,
            lng,
            at: Date.now(),
          };
        }
      } catch {
        // keep silent and preserve current fallback
      }
    };

    const timeoutId = setTimeout(fetchAddress, 250);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [markerPopupAddress, selectedVehiclePopupPosition, selectedVehicleRecord]);

  useEffect(() => {
    if (!markerPopupDeviceId || !selectedDevices.length) return;
    if (!selectedDevices.some((id) => String(id) === String(markerPopupDeviceId))) {
      setMarkerPopupDeviceId(null);
    }
  }, [markerPopupDeviceId, selectedDevices]);

  useEffect(() => {
    syncFocusedVehicleLivePosition(true);
    scheduleImperativeMarkerSync();
  }, [markerPopupDeviceId, selectedDevices, scheduleImperativeMarkerSync, syncFocusedVehicleLivePosition]);

  const getStatusTheme = useCallback((status) => {
    const normalized = String(status || "").toLowerCase();
    if (normalized === "running") {
      return { header: "#16a34a", soft: "#dcfce7", text: "#166534" };
    }
    if (normalized === "idle") {
      return { header: "#f59e0b", soft: "#fef3c7", text: "#92400e" };
    }
    if (normalized === "stopped") {
      return { header: "#ff1f4b", soft: "#ffe4ea", text: "#9f1239" };
    }
    return { header: "#475569", soft: "#e2e8f0", text: "#334155" };
  }, []);

  const handleClusterClick = useCallback((cluster) => {
    if (!cluster || !mapRef.current || !window.google?.maps?.LatLngBounds) return;
    const bounds = new window.google.maps.LatLngBounds();
    (Array.isArray(cluster.members) ? cluster.members : []).forEach((member) => {
      if (isValidMapCoordinate(member?.lat, member?.lng)) {
        bounds.extend({ lat: Number(member.lat), lng: Number(member.lng) });
      }
    });
    if (!bounds.isEmpty?.()) {
      mapRef.current.fitBounds(bounds, 80);
    } else {
      const clusterLat = Number(cluster?.lat ?? cluster?.latitude);
      const clusterLng = Number(cluster?.lng ?? cluster?.longitude);
      if (!isValidMapCoordinate(clusterLat, clusterLng)) return;
      mapRef.current.panTo({ lat: clusterLat, lng: clusterLng });
      const currentZoom = Number(mapRef.current.getZoom?.() || 0);
      mapRef.current.setZoom(Math.min(CLUSTER_MAX_AUTO_ZOOM, currentZoom + 2));
    }
    publishViewportSnapshot(mapRef.current);
  }, [publishViewportSnapshot]);

  const formatPopupTime = useCallback((value) => {
    const ts = toTimestampMs(value);
    if (!ts) return "-";
    return new Date(ts).toLocaleString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).replace(",", "");
  }, []);

  useEffect(() => {
    if (!map || !followMode || !followTargetVehicleLivePos) return;
    if (Date.now() < Number(followPauseUntilRef.current || 0)) return;
    const selectedMarkerId = String(selectedDevicesRef.current?.[0] || "");
    const shouldOffsetForPopup =
      Boolean(markerPopupDeviceIdRef.current) &&
      String(markerPopupDeviceIdRef.current) === selectedMarkerId;
    const rawTarget = {
      lat: followTargetVehicleLivePos.lat,
      lng: followTargetVehicleLivePos.lng,
    };
    const normalizedStatus = String(followTargetVehicleLivePos.status || "").toLowerCase();
    const resolvedSpeed = Math.max(0, Number(followTargetVehicleLivePos.speed || 0));
    const isMoving =
      resolvedSpeed > 1 ||
      ["running", "moving"].includes(normalizedStatus);
    const wasMoving = Boolean(followWasMovingRef.current);
    const justStopped = wasMoving && !isMoving;
    followWasMovingRef.current = isMoving;
    const viewportState = getMarkerViewportState(mapRef.current, rawTarget, {
      popupOpen: shouldOffsetForPopup,
      leftEdgeRatio: isMoving ? FOLLOW_MOVING_RECENTER_X_RATIO : FOLLOW_EDGE_TRIGGER_X_RATIO,
      rightEdgeRatio: isMoving ? FOLLOW_MOVING_RECENTER_X_RATIO : FOLLOW_EDGE_TRIGGER_X_RATIO,
      topEdgeRatio: isMoving ? FOLLOW_MOVING_RECENTER_Y_RATIO : FOLLOW_EDGE_TRIGGER_Y_RATIO,
      bottomEdgeRatio: isMoving ? FOLLOW_MOVING_RECENTER_Y_RATIO : FOLLOW_EDGE_TRIGGER_Y_RATIO,
    });
    if (!justStopped && viewportState && !viewportState.outsideSafeZone) {
      return;
    }
    latestFollowTargetRef.current =
      getMapCenterForMarkerAnchor(mapRef.current, rawTarget, {
        anchorYRatio: shouldOffsetForPopup ? POPUP_FOCUS_ANCHOR_Y_RATIO : 0.5,
      }) || rawTarget;

    if (followPanRafRef.current) return;

    followPanRafRef.current = requestAnimationFrame(() => {
      followPanRafRef.current = null;
      const target = latestFollowTargetRef.current;
      if (!target || !mapRef.current || !followMode) return;
      const center = mapRef.current.getCenter?.();
      const currentCenter = center
        ? { lat: center.lat(), lng: center.lng() }
        : null;
      if (!currentCenter || (!justStopped && haversineMeters(currentCenter, target) <= FOLLOW_PAN_MIN_METERS)) {
        return;
      }
      if (!isMoving) {
        mapRef.current.panTo(target);
        return;
      }
      const easedTarget = interpolatePoint(currentCenter, target, FOLLOW_PAN_EASE);
      mapRef.current.panTo(easedTarget);
    });
  }, [followMode, followTargetVehicleLivePos, map]);

  useEffect(() => {
    if (followMode) return undefined;
    latestFollowTargetRef.current = null;
    followWasMovingRef.current = false;
    if (followPanRafRef.current) {
      cancelAnimationFrame(followPanRafRef.current);
      followPanRafRef.current = null;
    }
    if (followResumeTimerRef.current) {
      clearTimeout(followResumeTimerRef.current);
      followResumeTimerRef.current = null;
    }
    followPauseUntilRef.current = 0;
    return undefined;
  }, [followMode]);

  const onDragStart = useCallback(() => {
    userInteractedRef.current = true;
    isPanningRef.current = true;
    if (followResumeTimerRef.current) {
      clearTimeout(followResumeTimerRef.current);
      followResumeTimerRef.current = null;
    }
    if (followMode) {
      followPauseUntilRef.current = Date.now() + 3000;
    }
  }, [followMode]);

  const onDragEnd = useCallback(() => {
    setTimeout(() => {
      isPanningRef.current = false;
    }, 100);
    if (!followMode) return;
    scheduleFollowResume();
  }, [followMode, scheduleFollowResume]);

  const directionsCallback = useCallback((res) => {
    if (res?.status === 'OK') {
      setDirectionsResponse(res);
      if (setRouteDetails) {
        const r = res.routes[0].legs[0];
        setRouteDetails({ distance: r.distance.text, duration: r.duration.text });
      }
    }
  }, [setRouteDetails]);

  useEffect(() => {
    if (!routeStart || !routeEnd) setDirectionsResponse(null);
  }, [routeStart, routeEnd]);

  const handleGeofenceClick = useCallback((geofence) => {
    if (geofence.deviceId && !selectedDevices.includes(geofence.deviceId)) {
      setSelectedDevices([geofence.deviceId]);
      const device = devicesOnMap.find(d => d._id === geofence.deviceId);
      if (device && handleDeviceClick) {
        handleDeviceClick(device);
      }
    }
  }, [selectedDevices, setSelectedDevices, devicesOnMap, handleDeviceClick]);

  useEffect(() => {
    if (!map || !selectedGeofence?.id || !window.google?.maps) return;
    const target = allGeofences.find(
      (item) => String(item.geofenceId || "") === String(selectedGeofence.id)
    );
    if (!target) return;

    if (target.type === "circle" && target.center) {
      map.panTo(target.center);
      map.setZoom(Math.max(map.getZoom() || 14, 15));
      return;
    }

    if (target.type === "polygon" && target.polygon?.coordinates?.[0]?.length) {
      const bounds = new window.google.maps.LatLngBounds();
      target.polygon.coordinates[0].forEach((coord) => {
        bounds.extend({ lat: coord[1], lng: coord[0] });
      });
      if (!bounds.isEmpty()) map.fitBounds(bounds, 80);
    }
  }, [map, allGeofences, selectedGeofence]);

  const handleVehicleMarkerClick = useCallback(
    (device) => {
      if (!device) return;
      setSelectedDevices([device._id]);
      setMarkerPopupAddress("");
      setMarkerPopupDeviceId(String(device._id));
      handleDeviceClick(device);
      if (setFollowMode) setFollowMode(true);
      userInteractedRef.current = false;
      isPanningRef.current = false;
    },
    [handleDeviceClick, setFollowMode, setSelectedDevices]
  );

  useEffect(() => {
    handleVehicleMarkerClickRef.current = handleVehicleMarkerClick;
  }, [handleVehicleMarkerClick]);

  return (
    <>
      <GoogleMap
        mapContainerStyle={containerStyle}
        onLoad={(m) => {
          setMap(m);
          mapRef.current = m;
          m.setCenter(defaultCenter);
          m.setZoom(DEFAULT_INDIA_ZOOM);
          publishViewportSnapshot(m);
          scheduleImperativeMarkerSync();
        }}
        onUnmount={() => {
          clearImperativeMarkers();
          setMap(null);
          mapRef.current = null;
        }}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onZoomChanged={onZoomChanged}
        onIdle={() => {
          publishViewportSnapshot(mapRef.current);
          scheduleImperativeMarkerSync();
        }}
        onClick={() => {
          setMarkerPopupAddress("");
          setMarkerPopupDeviceId(null);
          if (setFollowMode) setFollowMode(false);
          userInteractedRef.current = true;
        }}
        options={{
          disableDefaultUI: true,
          mapTypeId: mapType,
          gestureHandling: 'greedy',
          zoomControl: false,
          rotateControl: true,
          rotateControlOptions: {
            position: window.google.maps.ControlPosition.RIGHT_BOTTOM
          },
          maxZoom: 20,
          minZoom: 2,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        }}
      >
	      
        {showTraffic && <TrafficLayer />}

        {hasServerClusterBubbles
          ? clusterPresentation.clusters.map((cluster) => (
              <ServerClusterMarker
                key={String(cluster?._id || cluster?.id || `${cluster?.latitude ?? cluster?.lat}:${cluster?.longitude ?? cluster?.lng}`)}
                cluster={cluster}
                onClick={() => handleClusterClick(cluster)}
              />
            ))
          : null}

        {hasClientClusterBubbles
          ? clusterPresentation.clusters.map((cluster) => (
              <ServerClusterMarker
                key={String(cluster?.id || `${cluster?.lat}:${cluster?.lng}`)}
                cluster={cluster}
                onClick={() => handleClusterClick(cluster)}
              />
            ))
          : null}

        {landmarkPlacementActive && !previewLandmark ? (
          <DrawingManager
            drawingMode={window.google?.maps?.drawing?.OverlayType?.CIRCLE || null}
            options={{
              drawingControl: false,
              circleOptions: {
                fillColor: "#111827",
                fillOpacity: 0.16,
                strokeColor: "#111827",
                strokeOpacity: 0.85,
                strokeWeight: 2,
                clickable: false,
                editable: false,
                draggable: false,
                zIndex: 1150,
              },
            }}
            onCircleComplete={handleLandmarkCircleComplete}
          />
        ) : null}

        {showGeofences && allGeofences.map((geofence, idx) => (
          <GeofenceRenderer
            key={`geofence-${geofence.geofenceId || geofence.deviceId || idx}`}
            geofence={geofence}
            isSelected={geofence.isSelected}
            onClick={() => handleGeofenceClick(geofence)}
          />
        ))}

        {allLandmarks.map((landmark) => (
          <React.Fragment key={landmark.landmarkId}>
            <Circle
              center={{ lat: landmark.lat, lng: landmark.lng }}
              radius={Math.max(0, Number(landmark.radius || 30))}
              options={{
                fillColor: landmark.isSelected ? "#fb923c" : "#fdba74",
                fillOpacity: landmark.isSelected ? 0.18 : 0.12,
                strokeColor: landmark.isSelected ? "#ea580c" : "#f97316",
                strokeOpacity: 0.9,
                strokeWeight: landmark.isSelected ? 2 : 1,
                clickable: true,
                zIndex: landmark.isSelected ? 1100 : 900,
              }}
              onClick={() => onSelectLandmark?.(landmark)}
            />
            <OverlayView
              position={{ lat: landmark.lat, lng: landmark.lng }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                onClick={() => onSelectLandmark?.(landmark)}
                title={landmark?.name || "Landmark"}
                style={{
                  transform: "translate(-50%, -100%)",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <div
                  style={{
                    width: landmark.isSelected ? 18 : 16,
                    height: landmark.isSelected ? 18 : 16,
                    borderRadius: "50% 50% 50% 0",
                    transform: "rotate(-45deg)",
                    background: landmark.isSelected ? "#ea580c" : "#fb923c",
                    border: "2px solid #fff",
                    boxShadow: "0 8px 14px rgba(234,88,12,0.28)",
                  }}
                />
                <div
                  style={{
                    maxWidth: 140,
                    padding: "3px 8px",
                    borderRadius: 999,
                    background: "rgba(255,255,255,0.92)",
                    color: "#9a3412",
                    fontSize: 11,
                    fontWeight: 800,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    boxShadow: "0 8px 18px rgba(15,23,42,0.12)",
                  }}
                >
                  {landmark?.name || "Landmark"}
                </div>
              </div>
            </OverlayView>
          </React.Fragment>
        ))}

        {previewLandmark ? (
          <>
            <Circle
              center={{ lat: previewLandmark.lat, lng: previewLandmark.lng }}
              radius={Math.max(0, Number(previewLandmark.radius || 30))}
              options={{
                fillColor: "#111827",
                fillOpacity: 0.16,
                strokeColor: "#111827",
                strokeOpacity: 0.85,
                strokeWeight: 2,
                clickable: true,
                editable: true,
                draggable: true,
                zIndex: 1150,
              }}
              onLoad={(circle) => {
                previewCircleRef.current = circle;
              }}
              onUnmount={() => {
                previewCircleRef.current = null;
              }}
              onRadiusChanged={syncPreviewCircleToDraft}
              onCenterChanged={syncPreviewCircleToDraft}
              onDragEnd={syncPreviewCircleToDraft}
            />
            <OverlayView
              position={{ lat: previewLandmark.lat, lng: previewLandmark.lng }}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                style={{
                  transform: "translate(-50%, -100%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 4,
                  pointerEvents: "none",
                }}
              >
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: "50% 50% 50% 0",
                    transform: "rotate(-45deg)",
                    background: "#ef4444",
                    border: "2px solid #fff",
                    boxShadow: "0 8px 14px rgba(239,68,68,0.28)",
                  }}
                />
              </div>
            </OverlayView>
          </>
        ) : null}

        {selectedNotificationPosition && (
          <>
            <OverlayView
              position={selectedNotificationPosition}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                style={{
                  position: "relative",
                  width: 24,
                  height: 24,
                  transform: "translate(-50%, -100%)",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50% 50% 50% 0",
                    background: "#ef4444",
                    transform: "rotate(-45deg)",
                    boxShadow: "0 10px 18px rgba(239,68,68,0.35)",
                    border: "2px solid #ffffff",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "#ffffff",
                    transform: "translate(-50%, -50%)",
                  }}
                />
              </div>
            </OverlayView>
            <InfoWindow
              position={selectedNotificationPosition}
              onCloseClick={() => onClearSelectedNotification?.()}
            >
              <div style={{ minWidth: 220, display: "flex", gap: 8, alignItems: "center", fontSize: 12 }}>
                <strong>{selectedNotification?.deviceName || selectedNotification?.imei || "Device"}</strong>
                <span>{new Date(selectedNotification?.triggeredAt || selectedNotification?.timestamp || Date.now()).toLocaleString("en-IN")}</span>
                <span style={{ textTransform: "none" }}>
                  {String(selectedNotification?.alarmType || "event").replace(/[_-]+/g, " ")}
                </span>
              </div>
            </InfoWindow>
          </>
        )}

        {selectedVehiclePopupPosition && selectedVehicleRecord && (
          <OverlayView
            position={selectedVehiclePopupPosition}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div
              style={{
                width: 388,
                maxWidth: "min(388px, calc(100vw - 32px))",
                borderRadius: 14,
                overflow: "hidden",
                background: "#ffffff",
                boxShadow: "0 18px 42px rgba(15, 23, 42, 0.22)",
                border: "1px solid rgba(15, 23, 42, 0.08)",
                pointerEvents: "auto",
                position: "relative",
                transform: "translate(-50%, calc(-100% - 18px))",
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <div
                style={{
                  background: getStatusTheme(selectedVehicleRecord.status).header,
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "9px 13px",
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.1 }}>
                  {selectedVehicleRecord.device?.name || selectedVehicleRecord.device?.imei || "Vehicle"}
                </div>
                <button
                  onClick={() => {
                    setMarkerPopupAddress("");
                    setMarkerPopupDeviceId(null);
                    setFollowMode?.(false);
                  }}
                  style={{
                    width: 28,
                    height: 28,
                    border: "none",
                    background: "transparent",
                    color: "#fff",
                    cursor: "pointer",
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <CloseOutlinedIcon sx={{ fontSize: 19 }} />
                </button>
              </div>

              <div style={{ padding: "9px 12px 8px", textAlign: "center" }}>
                {[
                  ["Object", selectedVehicleRecord.device?.name || "-"],
                  ["Address", normalizeAddressText(selectedVehicleRecord.address) || "Resolving address..."],
                  [
                    "Status",
                    `${String(selectedVehicleRecord.status || "unknown").toLowerCase()}${selectedVehicleRecord.packetTime ? ` since ${formatPopupTime(selectedVehicleRecord.packetTime).split(" ")[1] || ""}` : ""}`,
                  ],
                  [
                    "Position",
                    `${Number(selectedVehiclePopupPosition.lat).toFixed(6)}, ${Number(selectedVehiclePopupPosition.lng).toFixed(6)}`,
                  ],
                  ["Speed", `${Number(selectedVehicleRecord.speed || 0).toFixed(0)} kmph`],
                  ["Time", formatPopupTime(selectedVehicleRecord.packetTime)],
                  ["Odometer", `${Number(selectedVehicleRecord.odometerKm || 0).toFixed(2)} km`],
                  ["Engine hours", `${Number(selectedVehicleRecord.engineHours || 0).toFixed(0)} h`],
                  [
                    "Voltage",
                    selectedVehicleRecord.voltage != null
                      ? `${Number(selectedVehicleRecord.voltage).toFixed(2)} v`
                      : "NA",
                  ],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "78px minmax(0, 1fr)",
                      gap: 6,
                      alignItems: "start",
                      marginBottom: 4,
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 800, color: "#111827" }}>
                      {label} :
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        lineHeight: 1.28,
                        color:
                          label === "Status"
                            ? getStatusTheme(selectedVehicleRecord.status).header
                            : "#1f2937",
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                        textAlign: "left",
                      }}
                    >
                      {value}
                    </div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  borderTop: "1px solid #e5e7eb",
                  padding: "10px 12px 12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {[
                  {
                    key: "edit",
                    icon: <EditOutlinedIcon sx={{ fontSize: 20, color: "#0f4fa8" }} />,
                    onClick: () => onEditDevice?.(selectedVehicleRecord.device),
                  },
                  {
                    key: "history",
                    icon: <HistoryOutlinedIcon sx={{ fontSize: 20, color: "#0f4fa8" }} />,
                    onClick: () => onShowDeviceHistory?.(selectedVehicleRecord.device),
                  },
                  {
                    key: "landmark",
                    icon: <AddLocationAltOutlinedIcon sx={{ fontSize: 20, color: "#0f4fa8" }} />,
                    onClick: () => {
                      if (typeof window !== "undefined") {
                        window.dispatchEvent(
                          new CustomEvent("gps-open-landmark-tab", {
                            detail: {
                              latitude: Number(selectedVehicleRecord?.latitude),
                              longitude: Number(selectedVehicleRecord?.longitude),
                              address: normalizeAddressText(selectedVehicleRecord?.address),
                              name:
                                selectedVehicleRecord?.device?.name ||
                                selectedVehicleRecord?.device?.vehicleNumber ||
                                "",
                            },
                          })
                        );
                      }
                    },
                  },
                  {
                    key: "geofence",
                    icon: <PolylineOutlinedIcon sx={{ fontSize: 20, color: "#0f4fa8" }} />,
                    onClick: () => onOpenGeofence?.(selectedVehicleRecord.device),
                  },
                  {
                    key: "command",
                    icon: <SettingsRemoteOutlinedIcon sx={{ fontSize: 20, color: "#0f4fa8" }} />,
                    onClick: () => onSendCommand?.(selectedVehicleRecord.device),
                  },
                ].map((action) => (
                  <button
                    key={action.key}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    title={action.key === "landmark" ? "Add Landmark" : action.key}
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: "50%",
                      border: "none",
                      background: "#cfe6ff",
                      display: "grid",
                      placeItems: "center",
                      cursor: action.disabled ? "not-allowed" : "pointer",
                      boxShadow: "0 6px 16px rgba(59, 130, 246, 0.16)",
                    }}
                  >
                    {action.icon}
                  </button>
                ))}
              </div>

              <div
                style={{
                  position: "absolute",
                  left: "50%",
                  bottom: -12,
                  width: 22,
                  height: 22,
                  background: "#ffffff",
                  transform: "translateX(-50%) rotate(45deg)",
                  borderRight: "1px solid rgba(15, 23, 42, 0.08)",
                  borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
                  boxShadow: "8px 8px 18px rgba(15, 23, 42, 0.08)",
                }}
              />
            </div>
          </OverlayView>
        )}

        {overlayRoutes.map((route) => (
          <React.Fragment key={`trip-route-${route.id}`}>
            <Polyline
              path={route.path}
              options={{
                strokeColor: route.isForcedRoute ? '#7c3aed' : '#1d4ed8',
                strokeOpacity: 0.9,
                strokeWeight: route.isForcedRoute ? 5 : 4,
                geodesic: false,
                icons: [
                  {
                    icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 },
                    offset: '0',
                    repeat: '14px',
                  },
                ],
              }}
            />
            <Circle
              center={route.start}
              radius={40}
              options={{
                fillColor: '#16a34a',
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 2,
              }}
            />
            <Circle
              center={route.end}
              radius={40}
              options={{
                fillColor: '#dc2626',
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 2,
              }}
            />
          </React.Fragment>
        ))}

        {routeStart && routeEnd && !directionsResponse && (
          <DirectionsService
            options={{
              origin: { lat: routeStart[0], lng: routeStart[1] },
              destination: { lat: routeEnd[0], lng: routeEnd[1] },
              travelMode: 'DRIVING'
            }}
            callback={directionsCallback}
          />
        )}

        {directionsResponse && <DirectionsRenderer directions={directionsResponse} />}

        {playbackData && (
          <TripPlaybackLayer
            route={playbackData.route}
            currentIndex={playbackData.currentIndex}
            currentPosition={playbackData.currentPosition}
            stops={playbackData.stops}
            status={
              playbackData.status ||
              playbackData.currentPosition?.vehicleStatus ||
              "running"
            }
            markerIcon={getMarkerAsset(
              {
                vehicleType: playbackData.vehicleType,
                deviceIcon: playbackData.deviceIcon,
                vehicleIcon: playbackData.vehicleIcon,
                icon: playbackData.icon,
              },
              playbackData.status ||
                playbackData.currentPosition?.vehicleStatus ||
                "running"
            )}
          />
        )}
      </GoogleMap>
    </>
  );
};

export default memo(MapContainer);
